import { excludeFromFilteringColumnProperty } from '@client-shared/ui-agGrid';

import PercentageOrRawRenderer from './cellRenderers/percentageOrRawCellRenderer.component';
import { DecimalRenderer, CustomRenderer, CustomDecimalRenderer, CustomRendererWithoutNull } from './numeralRenderers';
import DateCellRenderer from '../shared/agGrid/cellRenderers/dateCellRenderer.component';
import { specialTypeValueFormatter } from './columnDefinitions/companyAssignments';
import {
  alignLeftCellStyle,
  alignRightCellStyle,
  alignCenterCellStyle,
} from '@common/cellStyles';
import LinkCellRenderer from './cellRenderers/linkCellRenderer.component';
import TextCellRenderer from './cellRenderers/textCellRenderer.component';
import OwnershipCellRenderer from './cellRenderers/ownershipCellRenderer.component';
import RoleCellRenderer from './cellRenderers/roleCellRenderer.component';
import { RunStatusCellRenderer, RunStatusFilterCellRenderer } from './cellRenderers/runStatusCellRenderers.component';
import RunStatusIconCellRenderer from './cellRenderers/runStatusIconCellRenderer.component';
import { HeartbeatStatusCellRenderer, HeartbeatStatusFilterCellRenderer } from './cellRenderers/heartbeatStatusCellRenderer.component';
import HeartbeatStatusIconCellRenderer from './cellRenderers/heartbeatStatusIconCellRenderer.component';
import ConnectionRunStatusLinkCellRenderer from './cellRenderers/connectionRunStatusLinkCellRenderer.component';
import PublicFlagRenderer from './cellRenderers/publicFlagCellRenderer.component';
import BooleanFlagRenderer from './cellRenderers/booleanFlagCellRenderer.component';
import { compareAddOrUpdate } from '../administration/shared/importHelpers';
import CountryCodedOptionCellRenderer from './cellRenderers/countryCodeOptionCellRenderer';
import StringsArrayCellRenderer from './agGrid/cellRenderers/stringsArrayCellRenderer';
import { ALL_OPTION, YNOptions } from './constants';
import { getQuickFilterTextForStringArrayColumn } from './gridEdit/quickFilterUtils';
import CheckboxCellRenderer from './agGrid/cellRenderers/checkboxCellRenderer.component';
import CurrencyRateCellRenderer from './agGrid/cellRenderers/currencyRateCellRenderer.component';
import DiagnosticsSeverityCellRenderer from './agGrid/cellRenderers/diagnosticsSeverityCellRenderer.component';
import { JSDiagnosticsReportCellRenderer } from './agGrid/cellRenderers/diagnosticsReportCellRenderer.component';
import { OptimizedExclamationIconCellRenderer } from './cellRenderers/optimizedExclamationIconCellRenderer.component';
import { PercentageValueFormatter, PercentageOrRawValueFormatter } from './valueFormatters/percentageFormatters';
import { ColumnDefinition } from '@common/xlsxExport/types';
import { formatPeriodCell } from '@common/utils/formatPeriodCell';

export const importValidationColumnDefinition: ColumnDefinition = {
  headerName: 'Import Validation',
  field: 'IMPORT_VALIDATION',
  minWidth: 200,
  sort: 'asc',
  cellRenderer: OptimizedExclamationIconCellRenderer,
  cellStyle: {
    ...alignLeftCellStyle.cellStyle,
    'white-space': 'normal',
  },
};

export const companyCodeColumnDefinition: ColumnDefinition = {
  headerName: 'Company Code',
  field: 'companyCode',
  colId: 'companyCode',
  ...alignCenterCellStyle,
  minWidth: 120,
  width: 140,
};

export const companyCodeLinkColumnDefinition: ColumnDefinition = {
  ...companyCodeColumnDefinition,
  cellRenderer: LinkCellRenderer,
};

export const accountColumnDefinition: ColumnDefinition = {
  headerName: 'Account',
  field: 'account',
  ...alignCenterCellStyle,
  minWidth: 100,
};

export const tradingPartnerColumnDefinition: ColumnDefinition = {
  headerName: 'Trading Partner',
  field: 'tradingPartner',
  ...alignCenterCellStyle,
  minWidth: 70,
};

export const costCenterColumnDefinition: ColumnDefinition = {
  headerName: 'Cost Center',
  field: 'costCenter',
  ...alignCenterCellStyle,
  minWidth: 70,
};

export const geographyColumnDefinition: ColumnDefinition = {
  headerName: 'Geography',
  field: 'geography',
  ...alignCenterCellStyle,
  minWidth: 95,
};

export const projectColumnDefinition: ColumnDefinition = {
  headerName: 'Project',
  field: 'projectCode',
  ...alignCenterCellStyle,
  minWidth: 100,
};

export const referenceColumnDefinition: ColumnDefinition = {
  headerName: 'Reference',
  field: 'reference',
  ...alignCenterCellStyle,
  minWidth: 90,
};

export const bookTypeColumnDefinition: ColumnDefinition = {
  headerName: 'Book Type',
  field: 'bookType',
  ...alignCenterCellStyle,
  minWidth: 70,
};

export const productLineColumnDefinition: ColumnDefinition = {
  headerName: 'Product Line',
  field: 'productLine',
  ...alignCenterCellStyle,
  minWidth: 75,
};

export const enteredCurrencyColumnDefinition: ColumnDefinition = {
  headerName: 'Entered Currency',
  field: 'currency',
  ...alignCenterCellStyle,
  minWidth: 70,
};

export const enteredDebitColumnDefinition: ColumnDefinition = {
  headerName: 'Entered Debit',
  field: 'enteredDR',
  cellRenderer: DecimalRenderer,
  ...alignRightCellStyle,
  minWidth: 100,
};

export const customEnteredDebitColumnDefinition: ColumnDefinition = {
  ...enteredDebitColumnDefinition,
  cellRenderer: CustomRenderer,
};

export const enteredCreditColumnDefinition: ColumnDefinition = {
  headerName: 'Entered Credit',
  field: 'enteredCR',
  cellRenderer: DecimalRenderer,
  ...alignRightCellStyle,
  minWidth: 100,
};

export const customEnteredCreditColumnDefinition: ColumnDefinition = {
  ...enteredCreditColumnDefinition,
  cellRenderer: CustomRenderer,
};

export const accountDescriptionColumnDefinition: ColumnDefinition = {
  headerName: 'Account Description',
  field: 'accountDesc',
  ...alignLeftCellStyle,
  minWidth: 200,
};

export const legalEntityColumnDefinition: ColumnDefinition = {
  headerName: 'LE',
  field: 'legalEntity',
  minWidth: 100,
  maxWidth: 100,
  ...alignCenterCellStyle,
};

export const leNameColumnDefinition: ColumnDefinition = {
  headerName: 'LE Name',
  field: 'leName',
  minWidth: 100,
  maxWidth: 100,
  ...alignCenterCellStyle,
};

export const managementEntityColumnDefinition: ColumnDefinition = {
  headerName: 'ME',
  field: 'managementEntity',
  ...alignCenterCellStyle,
};

export const localAccountColumnDefinition: ColumnDefinition = {
  headerName: 'Account',
  field: 'localAccount',
  ...alignLeftCellStyle,
  minWidth: 80,
};

export const ileColumnDefinition: ColumnDefinition = {
  headerName: 'ILE',
  field: 'ile',
  ...alignLeftCellStyle,
};

export const imeColumnDefinition: ColumnDefinition = {
  headerName: 'IME',
  field: 'ime',
  ...alignCenterCellStyle,
};

export const localAccountDescriptionColumnDefinition: ColumnDefinition = {
  headerName: 'Account Description',
  field: 'localAccountDescription',
  minWidth: 200,
  ...alignLeftCellStyle,
};

export const countryTaxTypeColumnDefinition: ColumnDefinition = {
  headerName: 'Country Tax Type',
  field: 'countryTaxType',
  width: 100,
  minWidth: 90,
  ...alignCenterCellStyle,
};

export const descriptionColumnDefinition: ColumnDefinition = {
  headerName: 'Description',
  field: 'description',
  minWidth: 210,
  ...alignCenterCellStyle,
};

export const descrColumnDefinition: ColumnDefinition = {
  ...descriptionColumnDefinition,
  field: 'descr',
};

export const ccDescriptionColumnDefinition: ColumnDefinition = {
  headerName: 'Company Code Description',
  field: 'ccDescription',
  colId: 'ccDescription',
  ...alignLeftCellStyle,
  minWidth: 240,
};

export const tbeCcDescriptionColumnDefinition: ColumnDefinition = {
  headerName: 'TBE Company Code Description',
  field: 'tbeCcDescription',
  colId: 'tbeCcDescription',
  ...alignLeftCellStyle,
  minWidth: 240,
};

export const bookToTaxDifferenceDescriptionColumnDefinition: ColumnDefinition = {
  headerName: 'Description',
  field: 'description',
  colId: 'bttDescription',
  width: 300,
  minWidth: 300,
  ...alignCenterCellStyle,
};

export const taxRateColumnDefinition: ColumnDefinition = {
  headerName: 'Current Tax Rate',
  field: 'taxRate',
  width: 105,
  minWidth: 105,
  valueFormatter: PercentageValueFormatter,
  ...alignRightCellStyle,
};

export const deferredTaxRateColumnDefinition: ColumnDefinition = {
  headerName: 'Deferred Tax Rate',
  field: 'defTaxRate',
  width: 105,
  minWidth: 105,
  valueFormatter: PercentageValueFormatter,
  filterParams: {
    valueFormatter: PercentageValueFormatter,
  },
  ...alignRightCellStyle,
};

export const overrideDeferredTaxRateColumnDefinition: ColumnDefinition = {
  headerName: 'Override Deferred Tax Rate',
  field: 'overrideDefTaxRate',
  width: 145,
  minWidth: 145,
  valueFormatter: PercentageValueFormatter,
  customExport: {
    omitIfNull: true,
  },
  ...alignRightCellStyle,
};

export const overrideExistsColumnDefinition: ColumnDefinition = {
  headerName: 'Override Exists',
  field: 'overrideExists',
  minWidth: 60,
};

export const overrideTaxRateColumnDefinition: ColumnDefinition = {
  headerName: 'Override Current Tax Rate',
  field: 'overrideTaxRate',
  width: 145,
  minWidth: 145,
  valueFormatter: PercentageValueFormatter,
  customExport: {
    omitIfNull: true,
  },
  ...alignRightCellStyle,
};

export const utilizedTaxRateColumnDefinition: ColumnDefinition = {
  headerName: 'Utilized Tax Rate',
  field: 'utilizedTaxRate',
  valueFormatter: PercentageValueFormatter,
  width: 105,
  minWidth: 105,
  ...alignRightCellStyle,
};

export const customReportCurrencyColumnDefinition: ColumnDefinition = {
  headerName: 'Currency',
  field: 'currencyCode',
  width: 85,
  minWidth: 85,
};

export const taxCurrencyColumnDefinition: ColumnDefinition = {
  headerName: 'Tax Currency',
  field: 'currencyCode',
  ...alignRightCellStyle,
  width: 85,
  minWidth: 85,
};

export const balanceSheetColumnDefinition: ColumnDefinition = {
  headerName: 'Balance Sheet',
  field: 'bsAccount',
  ...alignCenterCellStyle,
  minWidth: 100,
};

export const taxDescriptionColumnDefinition: ColumnDefinition = {
  headerName: 'Tax Description',
  field: 'taxDescription',
  minWidth: 280,
  ...alignLeftCellStyle,
};

export const plAccountColumnDefinition: ColumnDefinition = {
  headerName: 'P&L',
  field: 'plAccount',
  ...alignCenterCellStyle,
  minWidth: 80,
};

export const exceptionAccountColumnDefinition: ColumnDefinition = {
  headerName: 'Exception Account',
  field: 'exceptionAccount',
  colId: 'exceptionAccount',
  minWidth: 90,
  width: 90,
  ...alignCenterCellStyle,
};

export const exceptionBasisColumnDefinition: ColumnDefinition = {
  headerName: 'Exception Basis',
  field: 'exceptionBasis',
  colId: 'exceptionBasis',
  minWidth: 90,
  width: 90,
  ...alignCenterCellStyle,
};

export const disallowedPercentageColumnDefinition: ColumnDefinition = {
  headerName: 'Disallowed Percentage',
  field: 'disallowedPercentage',
  colId: 'disallowedPercentage',
  valueFormatter: PercentageValueFormatter,
  width: 100,
  minWidth: 100,
  ...alignRightCellStyle,
};

export const deferredTaxAccountColumnDefinition: ColumnDefinition = {
  headerName: 'Deferred Tax Account',
  field: 'deferredTaxAccount',
  minWidth: 140,
  width: 140,
  ...alignCenterCellStyle,
};

export const runStartColumnDefinition: ColumnDefinition = {
  headerName: 'Run Start',
  field: 'runStart',
  sort: 'desc',
  cellRenderer: DateCellRenderer,
  ...alignLeftCellStyle,
};

export const totalTimeElapsedTimeColumnDefinition: ColumnDefinition = {
  headerName: 'Total Elapsed Time* (mm:ss)',
  field: 'runDuration',
};

export const importTrialBalancesColumnDefinition: ColumnDefinition = {
  headerName: 'Import Trial Balances',
  field: 'tbDuration',
};

export const validateTrialBalancesColumnDefinition: ColumnDefinition = {
  headerName: 'Validate Trial Balances',
  field: 'validDuration',
};

export const calculateTaxColumnDefinition: ColumnDefinition = {
  headerName: 'Calculate Tax',
  field: 'calcDuration',
};

export const runEndColumnDefinition: ColumnDefinition = {
  headerName: 'Run End',
  field: 'runEnd',
  cellRenderer: DateCellRenderer,
};

export const statusColumnDefinition: ColumnDefinition = {
  headerName: 'Status',
  field: 'statusName',
  ...alignLeftCellStyle,
  maxWidth: 100,
};

export const plAccountJournalsDetailColumnDefinition: ColumnDefinition = {
  headerName: 'P&L Account',
  field: 'plAccount',
  ...alignCenterCellStyle,
  minWidth: 80,
};

export const plAmountDescriptionColumnDefinition: ColumnDefinition = {
  headerName: 'P&L Amount Description',
  field: 'description',
  ...alignLeftCellStyle,
  minWidth: 200,
};

export const plAmountColumnDefinition: ColumnDefinition = {
  headerName: 'P&L Amount',
  field: 'plAmount',
  cellRenderer: DecimalRenderer,
  ...alignRightCellStyle,
  minWidth: 100,
};

export const uiAmountColumnDefinition: ColumnDefinition = {
  headerName: 'UI Amount',
  field: 'uiAmount',
  minWidth: 85,
  ...alignCenterCellStyle,
};

export const notesColumnDefinition: ColumnDefinition = {
  headerName: 'Notes',
  field: 'notes',
  ...alignLeftCellStyle,
  minWidth: 200,
};

export const connectionNameColumnDefinition: ColumnDefinition = {
  headerName: 'Connection Name',
  field: 'connectionName',
  ...alignLeftCellStyle,
  minWidth: 200,
};

export const adminColumnSharedFields: ColumnDefinition = {
  colId: 'adminCol',
  headerName: '',
  minWidth: 60,
  maxWidth: 85,
  ...excludeFromFilteringColumnProperty,
};

export const reclassBsAccountColumnDefinition: ColumnDefinition = {
  headerName: 'Reclass Balance Sheet',
  field: 'reclassBsAccount',
  ...alignCenterCellStyle,
};

export const reclassPlAccountColumnDefinition: ColumnDefinition = {
  headerName: 'Reclass P&L',
  field: 'reclassPlAccount',
  ...alignCenterCellStyle,
};

export const goldIdColumnDefintion: ColumnDefinition = {
  headerName: 'GOLD ID',
  field: 'goldId',
  minWidth: 80,
  ...alignCenterCellStyle,
};

export const lineOfBusinessColumnDefinition: ColumnDefinition = {
  headerName: 'Line of Business',
  field: 'lineOfBusiness',
  width: 80,
  minWidth: 80,
  ...alignCenterCellStyle,
  ...excludeFromFilteringColumnProperty,
};

export const enabledFlagColumnDefinition: ColumnDefinition = {
  headerName: 'Enabled Flag',
  field: 'enabledFlag',
  colId: 'enabledFlag',
  width: 70,
  minWidth: 70,
  ...alignCenterCellStyle,
  ...excludeFromFilteringColumnProperty,
};

export const maintenanceFlagColumnDefinition: ColumnDefinition = {
  headerName: 'Maintenance Flag',
  field: 'maintenanceFlag',
  colId: 'maintenanceFlag',
  width: 70,
  minWidth: 70,
  ...alignCenterCellStyle,
  ...excludeFromFilteringColumnProperty,
};

export const bookToTaxCodeTagCodeColumnDefinition: ColumnDefinition = {
  headerName: 'Tag Code',
  field: 'tagCode',
  width: 120,
  minWidth: 80,
  ...alignLeftCellStyle,
};

export const bookToTaxCodeDescriptionColumnDefinition: ColumnDefinition = {
  headerName: 'Book to Tax Code Description',
  field: 'tagDescription',
  colId: 'tagDescription',
  width: 400,
  minWidth: 380,
  ...alignLeftCellStyle,
};

export const bookToTaxCodeDescriptionCustomReportsColumnDefinition: ColumnDefinition = {
  headerName: 'Book to Tax Code Description',
  field: 'bookToTaxCodeDescription',
  colId: 'bookToTaxCodeDescription',
  width: 105,
  minWidth: 105,
  ...alignLeftCellStyle,
};

export const exceptionIdColumnDefinition: ColumnDefinition = {
  headerName: 'Exception ID',
  field: 'exceptionId',
  width: 80,
  minWidth: 80,
  ...alignCenterCellStyle,
};

export const countryCodeColumnDefinition: ColumnDefinition = {
  headerName: 'Country Code',
  field: 'countryCode',
  minWidth: 75,
  ...alignCenterCellStyle,
};

export const oneByOneBalanceColumnDefinition: ColumnDefinition = {
  headerName: '1/1 Balance',
  field: 'begAmount',
  cellRenderer: CustomDecimalRenderer,
  width: 100,
  minWidth: 100,
  ...alignRightCellStyle,
};

export const ytdAmountColumnDefinition: ColumnDefinition = {
  headerName: 'YTD Amount',
  field: 'ytdAmount',
  cellRenderer: CustomDecimalRenderer,
  width: 140,
  minWidth: 120,
  ...alignRightCellStyle,
};

export const ytdBalanceColumnDefinition: ColumnDefinition = {
  headerName: 'YTD Balance',
  field: 'ytdBalance',
  cellRenderer: CustomRenderer,
  minWidth: 120,
  ...alignRightCellStyle,
};

export const exclusionPercentageColumnDefinition: ColumnDefinition = {
  headerName: 'Exclusion Percentage',
  field: 'disallowedPer',
  cellRenderer: TextCellRenderer,
  width: 100,
  minWidth: 100,
  ...alignRightCellStyle,
};

export const disallowedAmountColumnDefinition: ColumnDefinition = {
  headerName: 'Disallowed Amount',
  cellRenderer: CustomDecimalRenderer,
  field: 'amount',
  width: 110,
  minWidth: 100,
  ...alignRightCellStyle,
};

export const disallowedAmountTotalColumnDefinition: ColumnDefinition = {
  headerName: 'Total',
  cellRenderer: CustomDecimalRenderer,
  field: 'disallowedAmtTotal',
  width: 110,
  minWidth: 100,
  ...alignRightCellStyle,
};

export const adjustmentTypeColumnDefinition: ColumnDefinition = {
  headerName: 'Adjustment Type',
  field: 'adjustmentType',
  width: 180,
  minWidth: 180,
  ...alignLeftCellStyle,
};

export const amountFromColumnDefinition: ColumnDefinition = {
  headerName: 'Amount From',
  field: 'amountFrom',
  cellRenderer: TextCellRenderer,
  minWidth: 80,
  ...alignLeftCellStyle,
};

export const balanceColumnDefinition: ColumnDefinition = {
  headerName: 'Balance',
  field: 'balance',
  cellRenderer: CustomDecimalRenderer,
  width: 120,
  minWidth: 120,
  ...alignRightCellStyle,
};

export const multiplierColumnDefinition: ColumnDefinition = {
  headerName: 'Multiplier',
  cellRenderer: TextCellRenderer,
  field: 'multiplier',
  width: 80,
  minWidth: 80,
  ...alignRightCellStyle,
};

export const manualAmountColumnDefinition: ColumnDefinition = {
  headerName: 'Manual Amount',
  field: 'manualAmount',
  cellRenderer: CustomDecimalRenderer,
  width: 120,
  minWidth: 120,
  ...alignRightCellStyle,
};

export const recurrenceFrequency: ColumnDefinition = {
  headerName: 'Recurrence Frequency',
  field: 'frequency',
  cellRenderer: TextCellRenderer,
  width: 120,
  minWidth: 120,
  ...alignCenterCellStyle,
};

export const annualCumulative: ColumnDefinition = {
  headerName: 'Annual Cumulative',
  field: 'annualCum',
  cellRenderer: TextCellRenderer,
  width: 120,
  minWidth: 120,
  ...alignCenterCellStyle,
};

export const currencyColumnDefinition: ColumnDefinition = {
  headerName: 'Currency',
  field: 'currency',
  minWidth: 80,
  ...alignCenterCellStyle,
};

export const enteredAmountColumnDefinition: ColumnDefinition = {
  headerName: 'Entered Balance',
  field: 'ENTERED_AMOUNT',
  cellRenderer: CustomRenderer,
  ...alignRightCellStyle,
};

export const taxYearColumnDefinition: ColumnDefinition = {
  headerName: 'Tax Year',
  field: 'taxYear',
  width: 80,
  minWidth: 80,
  aggFunc: null,
  cellRenderer: null,
};

export const entryTypeColumnDefinition: ColumnDefinition = {
  headerName: 'Entry Type',
  field: 'taxEntryTypeDescription',
  width: 140,
  ...alignLeftCellStyle,
  minWidth: 140,
};

export const amountColumnDefinition: ColumnDefinition = {
  headerName: 'Amount',
  field: 'amount',
  width: 100,
  cellRenderer: CustomDecimalRenderer,
  minWidth: 100,
  ...alignRightCellStyle,
};

export const activityColumnDefinition: ColumnDefinition = {
  headerName: 'Activity',
  field: 'activity',
  width: 140,
  minWidth: 140,
  ...alignLeftCellStyle,
};

export const entryDateColumnDefinition: ColumnDefinition = {
  headerName: 'Created Date',
  field: 'createdOn',
  cellRenderer: DateCellRenderer,
  width: 140,
  minWidth: 125,
};

export const preparerColumnDefinition: ColumnDefinition = {
  headerName: 'Preparer',
  field: 'preparerName',
  width: 180,
  minWidth: 120,
  ...alignLeftCellStyle,
};

export const methodColumnDefinition: ColumnDefinition = {
  headerName: 'Method',
  field: 'method',
  width: 90,
  ...alignLeftCellStyle,
  minWidth: 100,
};

export const topsideFlagColumnDefinition: ColumnDefinition = {
  headerName: 'Topside',
  field: 'topsideFlag',
  width: 80,
  minWidth: 80,
};

export const postedFlagColumnDefinition: ColumnDefinition = {
  headerName: 'GL Post',
  field: 'postedFlag',
  width: 60,
  minWidth: 60,
};

export const reviewerColumnDefinition: ColumnDefinition = {
  headerName: 'Reviewer',
  field: 'reviewerName',
  width: 180,
  minWidth: 120,
  ...alignLeftCellStyle,
};

export const slAccountColumnDefinition: ColumnDefinition = {
  headerName: 'Subledger Account',
  field: 'slAccount',
  minWidth: 120,
  ...alignLeftCellStyle,
};

export const netAmountColumnDefinition: ColumnDefinition = {
  headerName: 'Net Amount',
  field: 'netAmount',
  cellRenderer: CustomDecimalRenderer,
  ...alignRightCellStyle,
  minWidth: 100,
};

export const grossAmountColumnDefinition: ColumnDefinition = {
  headerName: 'Gross Amount',
  field: 'grossAmount',
  cellRenderer: CustomDecimalRenderer,
  ...alignRightCellStyle,
  minWidth: 100,
};

export const grossFlagColumnDefinition: ColumnDefinition = {
  headerName: 'Gross or Net',
  field: 'grossOrNet',
  ...alignCenterCellStyle,
  minWidth: 100,
};

export const fbosFlagColumnDefinition: ColumnDefinition = {
  headerName: 'Generate matching State and FBOS Entries',
  field: 'fbosFlag',
  cellRenderer: BooleanFlagRenderer,
  width: 180,
  minWidth: 160,
  ...alignCenterCellStyle,
};

export const postDateColumnDefinition: ColumnDefinition = {
  headerName: 'Post Date',
  field: 'postDate',
  minWidth: 125,
  cellRenderer: DateCellRenderer,
};

export const NOLFlagColumnDefinition: ColumnDefinition = {
  headerName: 'NOL Flag',
  field: 'NOLFlag',
  minWidth: 30,
  ...alignCenterCellStyle,
};

export const VAFlagColumnDefinition: ColumnDefinition = {
  headerName: 'VA Flag',
  field: 'VAFlag',
  minWidth: 30,
  ...alignCenterCellStyle,
};

export const beginningBalanceColumnDefinition: ColumnDefinition = {
  headerName: 'Beginnning Balance',
  field: 'beginningBalance',
  minWidth: 100,
  cellRenderer: CustomRenderer,
  ...alignRightCellStyle,
};

export const currentYearLossColumnDefinition: ColumnDefinition = {
  headerName: 'Current Year Loss',
  field: 'currentYearLoss',
  minWidth: 100,
  cellRenderer: CustomRenderer,
  ...alignRightCellStyle,
};

export const NOLCarryforwardUtilizedColumDefinition: ColumnDefinition = {
  headerName: 'NOL Carryforward Utilized',
  field: 'NOLCarryforwardUtilized',
  minWidth: 100,
  cellRenderer: CustomRenderer,
  ...alignRightCellStyle,
};

export const expiredColumnDefinition: ColumnDefinition = {
  headerName: 'Expired',
  field: 'expired',
  minWidth: 100,
  ...alignRightCellStyle,
};

export const beginBalanceColumnDefinition: ColumnDefinition = {
  headerName: 'Begin Balance',
  field: 'beginBalance',
  minWidth: 100,
  cellRenderer: CustomRenderer,
  ...alignRightCellStyle,
};

export const rateChangeColumnDefinition: ColumnDefinition = {
  headerName: 'Rate Change',
  field: 'rateChange',
  minWidth: 100,
  cellRenderer: CustomRenderer,
  ...alignRightCellStyle,
};

export const returnToAccrualColumnDefinition: ColumnDefinition = {
  headerName: 'Return to Accrual',
  field: 'returnToAccrual',
  minWidth: 100,
  cellRenderer: CustomRenderer,
  ...alignRightCellStyle,
};

export const auditTrueUpColumnDefinition: ColumnDefinition = {
  headerName: 'Audit True Up',
  field: 'auditTrueUp',
  minWidth: 100,
  ...alignRightCellStyle,
};

export const dtaDtlAdjustmentColumnDefinition: ColumnDefinition = {
  headerName: 'DTA DTL Adjustment',
  field: 'dtaDtlAdjustment',
  minWidth: 100,
  ...alignRightCellStyle,
};

export const postReturnAdjustmentColumnDefinition: ColumnDefinition = {
  headerName: 'Post Return Adjustment',
  field: 'postReturnAdjustment',
  minWidth: 100,
  cellRenderer: CustomRenderer,
  ...alignRightCellStyle,
};

export const balanceSheetOtherColumnDefinition: ColumnDefinition = {
  headerName: 'Balance Sheet Other',
  field: 'balanceSheetOther',
  minWidth: 100,
  cellRenderer: CustomRenderer,
  ...alignRightCellStyle,
};

export const adjustedBeginningBalanceColumnDefinition: ColumnDefinition = {
  headerName: 'Adjusted Beginning Balance',
  field: 'adjustedBeginningBalance',
  minWidth: 100,
  cellRenderer: CustomRenderer,
  ...alignRightCellStyle,
};

export const cyActivityColumnDefinition: ColumnDefinition = {
  headerName: 'CY Activity',
  field: 'cyActivity',
  minWidth: 100,
  cellRenderer: CustomRenderer,
  ...alignRightCellStyle,
};

export const reclassColumnDefinition: ColumnDefinition = {
  headerName: 'Reclass',
  field: 'reclass',
  minWidth: 100,
  cellRenderer: CustomRenderer,
  ...alignRightCellStyle,
};

export const equityColumnDefinition: ColumnDefinition = {
  headerName: 'Equity',
  field: 'equity',
  minWidth: 100,
  cellRenderer: CustomRenderer,
  ...alignRightCellStyle,
};

export const goodwillColumnDefinition: ColumnDefinition = {
  headerName: 'Goodwill',
  field: 'goodwill',
  minWidth: 100,
  cellRenderer: CustomRenderer,
  ...alignRightCellStyle,
};

export const otherComprehensiveIncomeColumnDefinition: ColumnDefinition = {
  headerName: 'Other Comprehensive Income',
  field: 'otherComprehensiveIncome',
  minWidth: 100,
  cellRenderer: CustomRenderer,
  ...alignRightCellStyle,
};

export const fxColumnDefinition: ColumnDefinition = {
  headerName: 'FX',
  field: 'fx',
  minWidth: 100,
  cellRenderer: CustomRenderer,
  ...alignRightCellStyle,
};

export const otherAdjustmentsColumnDefinition: ColumnDefinition = {
  headerName: 'Other Adjustments',
  field: 'otherAdjustments',
  minWidth: 100,
  cellRenderer: CustomRenderer,
  ...alignRightCellStyle,
};

export const endingBalanceColumnDefinition: ColumnDefinition = {
  headerName: 'Ending Balance',
  field: 'endingBalance',
  minWidth: 100,
  cellRenderer: CustomRenderer,
  ...alignRightCellStyle,
};

export const r2rAccountColumnDefinition: ColumnDefinition = {
  headerName: 'Account',
  field: 'r2rAccount',
  width: 100,
  minWidth: 80,
  ...alignLeftCellStyle,
};

export const r2rDescriptionColumnDefinition: ColumnDefinition = {
  headerName: 'Description',
  field: 'r2rDescription',
  minWidth: 450,
  ...alignLeftCellStyle,
};

export const r2RAccountColumnDefinition: ColumnDefinition = {
  headerName: 'Account',
  field: 'r2RAccount',
  width: 100,
  minWidth: 80,
  ...alignLeftCellStyle,
};

export const r2RDescriptionColumnDefinition: ColumnDefinition = {
  headerName: 'Description',
  field: 'r2RDescription',
  minWidth: 450,
  ...alignLeftCellStyle,
};

export const businessColumnDefinition: ColumnDefinition = {
  headerName: 'Business',
  field: 'businessDescription',
  minWidth: 120,
  ...alignLeftCellStyle,
};

export const businessUnitColumnDefinition: ColumnDefinition = {
  headerName: 'Business Unit',
  field: 'businessUnit',
  minWidth: 120,
  ...alignCenterCellStyle,
};

export const buNameColumnDefinition: ColumnDefinition = {
  headerName: 'BU Name',
  field: 'buName',
  minWidth: 100,
  maxWidth: 100,
  ...alignCenterCellStyle,
};

export const businessUnitDescriptionColumnDefinition: ColumnDefinition = {
  headerName: 'Business Unit Description',
  field: 'businessUnitDescription',
  ...alignLeftCellStyle,
};

export const marsAccountColumnDefinition: ColumnDefinition = {
  headerName: 'MARS Account',
  field: 'marsAccount',
  width: 100,
  minWidth: 80,
  ...alignCenterCellStyle,
};

export const marsAccountDescriptionColumnDefiniton: ColumnDefinition = {
  headerName: 'MARS Account Description',
  field: 'marsDescription',
  width: 280,
  minWidth: 260,
  ...alignLeftCellStyle,
};

export const businessCodeColumnDefinition: ColumnDefinition = {
  headerName: 'Business Code',
  field: 'businessCode',
  minWidth: 100,
  ...alignCenterCellStyle,
};

export const businessDescriptionColumnDefinition: ColumnDefinition = {
  headerName: 'Business Description',
  field: 'businessDescription',
  width: 240,
  minWidth: 120,
  ...alignLeftCellStyle,
};

export const ancestorListColumnDefinition: ColumnDefinition = {
  headerName: 'Ancestor List',
  field: 'ancestorList',
  ...alignLeftCellStyle,
};

export const chartNameColumnDefinition: ColumnDefinition = {
  headerName: 'Chart Name',
  field: 'chartName',
  width: 240,
  minWidth: 160,
  ...alignCenterCellStyle,
};

export const associatedLeCountryCodeColumnDefinition: ColumnDefinition = {
  headerName: 'Associated LE Country Code',
  field: 'associatedLeCountryCode',
  width: 180,
  minWidth: 125,
  ...alignCenterCellStyle,
};

export const associatedLeGoldIdColumnDefinition: ColumnDefinition = {
  headerName: 'Associated LE GOLD ID',
  field: 'associatedLeGoldId',
  width: 180,
  minWidth: 120,
  ...alignCenterCellStyle,
};

export const associatedLeUsTaxTypeColumnDefinition: ColumnDefinition = {
  headerName: 'Associated LE US Tax Type',
  field: 'associatedLeUsTaxType',
  width: 180,
  minWidth: 125,
  ...alignCenterCellStyle,
};

export const associatedLeLocalTaxTypeColumnDefinition: ColumnDefinition = {
  headerName: 'Associated LE Local Tax Type',
  field: 'associatedLeLocalTaxType',
  width: 180,
  minWidth: 125,
  ...alignCenterCellStyle,
};

export const overrideUsTaxTypeColumnDefinition: ColumnDefinition = {
  headerName: 'Override US Tax Type',
  field: 'overrideUsTaxType',
  width: 180,
  minWidth: 125,
  ...alignCenterCellStyle,
};

export const overrideLocalTaxTypeColumnDefinition: ColumnDefinition = {
  headerName: 'Override Local Tax Type',
  field: 'overrideLocalTaxType',
  width: 180,
  minWidth: 125,
  ...alignCenterCellStyle,
};

export const functionalCurrencyColumnDefinition: ColumnDefinition = {
  headerName: 'Functional Currency',
  field: 'functionalCurrency',
  width: 180,
  minWidth: 120,
  ...alignCenterCellStyle,
};

export const overrideTaxCurrencyColumnDefinition: ColumnDefinition = {
  headerName: 'Override Tax Currency',
  field: 'overrideTaxCurrency',
  width: 180,
  minWidth: 120,
  ...alignCenterCellStyle,
};

export const associatedLeBranchColumnDefinition: ColumnDefinition = {
  headerName: 'Branch',
  field: 'associatedLeBranch',
  width: 120,
  minWidth: 100,
  ...alignCenterCellStyle,
};

export const homeOfficeCompanyCodeColumnDefinition: ColumnDefinition = {
  headerName: 'Home Office Company Code',
  field: 'homeOfficeCompanyCode',
  width: 180,
  minWidth: 140,
  ...alignLeftCellStyle,
};

export const homeOfficeCountryCodeColumnDefinition: ColumnDefinition = {
  headerName: 'Home Office Country Code',
  field: 'homeOfficeCountryCode',
  width: 180,
  minWidth: 140,
  ...alignCenterCellStyle,
};

export const branchColumnDefinition: ColumnDefinition = {
  headerName: 'Branch',
  field: 'branch',
  width: 80,
  minWidth: 80,
  ...alignCenterCellStyle,
};

export const recordFTCColumnDefinition: ColumnDefinition = {
  headerName: 'Record FTC',
  field: 'recordFTC',
  width: 140,
  minWidth: 120,
  ...alignCenterCellStyle,
};

export const usShareholderFlagColumnDefinition: ColumnDefinition = {
  headerName: 'US Shareholder Flag',
  field: 'usShareholderFlag',
  width: 140,
  minWidth: 140,
  ...alignCenterCellStyle,
};

export const ledgerIdColumnDefinition: ColumnDefinition = {
  headerName: 'Ledger ID',
  field: 'ledgerId',
  width: 120,
  minWidth: 100,
  ...alignRightCellStyle,
};

export const ledgerShortNameColumnDefinition: ColumnDefinition = {
  headerName: 'Ledger Short Name',
  field: 'ledgerShortName',
  width: 140,
  minWidth: 120,
  ...alignLeftCellStyle,
};

export const sourceColumnDefinition: ColumnDefinition = {
  headerName: 'Source',
  field: 'source',
  width: 140,
  minWidth: 120,
  ...alignLeftCellStyle,
};

export const subledgerFlagColumnDefinition: ColumnDefinition = {
  headerName: 'BSM Flag',
  field: 'subledgerFlag',
  width: 140,
  minWidth: 120,
  ...alignCenterCellStyle,
};

export const statCalcColumnDefinition: ColumnDefinition = {
  headerName: 'STAT Calc Flag',
  field: 'statCalc',
  width: 140,
  minWidth: 120,
  ...alignCenterCellStyle,
};

export const statCurrencyColumnDefinition: ColumnDefinition = {
  headerName: 'STAT Currency',
  field: 'statCurrency',
  width: 140,
  minWidth: 120,
  ...alignCenterCellStyle,
};

export const statLedgerColumnDefinition: ColumnDefinition = {
  headerName: 'STAT Ledger',
  field: 'statLedger',
  width: 140,
  minWidth: 120,
  ...alignLeftCellStyle,
};

export const profitCenterColumnDefinition: ColumnDefinition = {
  headerName: 'Profit Center',
  field: 'profitCenter',
  width: 140,
  minWidth: 120,
  ...alignCenterCellStyle,
};

export const functionalAreaColumnDefinition: ColumnDefinition = {
  headerName: 'Functional Area',
  field: 'functionalArea',
  width: 160,
  minWidth: 120,
  ...alignLeftCellStyle,
};

export const wbsColumnDefinition: ColumnDefinition = {
  headerName: 'WBS',
  field: 'wbs',
  width: 120,
  minWidth: 100,
  ...alignLeftCellStyle,
};

export const ledgerIdInColumnDefinition: ColumnDefinition = {
  headerName: 'Ledger ID In',
  field: 'ledgerIdIn',
  width: 140,
  minWidth: 120,
  ...alignLeftCellStyle,
};

export const ledgerIdOutColumnDefinition: ColumnDefinition = {
  headerName: 'Ledger ID Out',
  field: 'ledgerIdOut',
  width: 140,
  minWidth: 120,
  ...alignLeftCellStyle,
};

export const funcCurrencyOverrideColumnDefinition: ColumnDefinition = {
  headerName: 'Functional Currency Override',
  field: 'funcCurrencyOverride',
  width: 180,
  minWidth: 145,
  ...alignCenterCellStyle,
};

export const leOverrideColumnDefinition: ColumnDefinition = {
  headerName: 'Legal Entity Override',
  field: 'leOverride',
  width: 140,
  minWidth: 120,
  ...alignCenterCellStyle,
};

export const ccOverrideColumnDefinition: ColumnDefinition = {
  headerName: 'Company Code Override',
  field: 'ccOverride',
  width: 140,
  minWidth: 125,
  ...alignLeftCellStyle,
};

export const jeFlagColumnDefinition: ColumnDefinition = {
  headerName: 'Allow Journals',
  field: 'jeFlag',
  width: 140,
  minWidth: 120,
  ...alignCenterCellStyle,
};

export const countryNameColumnDefinition: ColumnDefinition = {
  headerName: 'Country Name',
  field: 'countryName',
  width: 240,
  minWidth: 120,
  ...alignLeftCellStyle,
};

export const currencyCodeColumnDefinition: ColumnDefinition = {
  headerName: 'Currency Code',
  field: 'currencyCode',
  ...alignCenterCellStyle,
};

export const precisionColumnDefinition: ColumnDefinition = {
  headerName: 'Precision',
  field: 'precision',
  ...alignCenterCellStyle,
};

export const customGroupNameColumnDefinition: ColumnDefinition = {
  headerName: 'Custom Group Name',
  field: 'name',
  width: 120,
  minWidth: 100,
  maxWidth: 158,
  ...alignLeftCellStyle,
};

export const publicFlagColumnDefinition: ColumnDefinition = {
  headerName: 'Public/Private',
  field: 'publicFlag',
  minWidth: 50,
  maxWidth: 106,
  cellRenderer: PublicFlagRenderer,
  ...alignLeftCellStyle,
};

export const itemsStringColumnDefinition: ColumnDefinition = {
  headerName: 'Items',
  field: 'itemsString',
  minWidth: 405,
  // tooltip: value => value.value,
  ...alignLeftCellStyle,
};

export const createdByColumnDefinition: ColumnDefinition = {
  headerName: 'Created By',
  field: 'createdBy',
  width: 120,
  minWidth: 100,
  ...alignLeftCellStyle,
};

export const updatedByColumnDefinition: ColumnDefinition = {
  headerName: 'Updated By',
  field: 'updatedBy',
  width: 120,
  minWidth: 100,
  ...alignLeftCellStyle,
};

export const updatedOnColumnDefinition: ColumnDefinition = {
  headerName: 'Updated Date',
  field: 'updatedOn',
  width: 140,
  minWidth: 100,
  cellRenderer: DateCellRenderer,
  ...alignLeftCellStyle,
};

export const postedOnColumnDefinition: ColumnDefinition = {
  headerName: 'Posted On',
  field: 'postedOn',
  width: 140,
  minWidth: 125,
  cellRenderer: DateCellRenderer,
  ...alignLeftCellStyle,
};

export const reviewedOnColumnDefinition: ColumnDefinition = {
  headerName: 'Reviewed On',
  field: 'reviewedOn',
  width: 140,
  minWidth: 125,
  cellRenderer: DateCellRenderer,
  ...alignLeftCellStyle,
};

export const typeColumnDefinition: ColumnDefinition = {
  headerName: 'Type',
  field: 'type',
  minWidth: 50,
  ...alignCenterCellStyle,
};

export const gaapColumnDefinition: ColumnDefinition = {
  headerName: 'GAAP',
  field: 'gaap',
  minWidth: 50,
  ...alignCenterCellStyle,
};

export const g2sColumnDefinition: ColumnDefinition = {
  headerName: 'G2S',
  field: 'g2s',
  minWidth: 50,
  ...alignCenterCellStyle,
};

export const lineColumnDefinition: ColumnDefinition = {
  headerName: 'Line',
  field: 'line',
  minWidth: 50,
  ...alignCenterCellStyle,
};

export const directionColumnDefinition: ColumnDefinition = {
  headerName: 'Direction',
  field: 'direction',
  minWidth: 70,
  ...alignCenterCellStyle,
};

export const glAccountColumnDefinition: ColumnDefinition = {
  headerName: 'GL Account',
  field: 'glAccount',
  minWidth: 80,
  ...alignCenterCellStyle,
};

export const glAccountCurrentColumnDefinition: ColumnDefinition = {
  headerName: 'GL Account Current',
  field: 'glAccountCurrent',
  minWidth: 180,
  ...alignLeftCellStyle,
};

export const glAccountPriorColumnDefinition: ColumnDefinition = {
  headerName: 'GL Account Prior',
  field: 'glAccountPrior',
  minWidth: 180,
  ...alignLeftCellStyle,
};

export const glAccount2ndPriorColumnDefinition: ColumnDefinition = {
  headerName: 'GL Account 2nd Prior',
  field: 'glAccount2ndPrior',
  minWidth: 180,
  ...alignLeftCellStyle,
};

export const debitUsdColumnDefinition: ColumnDefinition = {
  headerName: 'Debit USD',
  field: 'debitUsd',
  minWidth: 80,
  ...alignCenterCellStyle,
};

export const creditUsdColumnDefinition: ColumnDefinition = {
  headerName: 'Credit USD',
  field: 'creditUsd',
  minWidth: 80,
  ...alignCenterCellStyle,
};

export const legalEntityDescriptionColumnDefinition: ColumnDefinition = {
  headerName: 'Legal Entity Description',
  field: 'leDescription',
  minWidth: 280,
  ...alignLeftCellStyle,
};

export const esTaxTypeColumnDefinition: ColumnDefinition = {
  headerName: 'ES Tax Type',
  field: 'esTaxType',
  minWidth: 80,
  ...alignCenterCellStyle,
};

export const esAltTaxTypeColumnDefinition: ColumnDefinition = {
  headerName: 'ES Alt Tax Type',
  field: 'esAltTaxType',
  minWidth: 105,
  ...alignCenterCellStyle,
};
export const usTaxTypeColumnDefinition: ColumnDefinition = {
  headerName: 'US Tax Type',
  field: 'usTaxType',
  minWidth: 80,
  ...alignCenterCellStyle,
};
export const localTaxTypeColumnDefinition: ColumnDefinition = {
  headerName: 'Local Tax Type',
  field: 'localTaxType',
  minWidth: 105,
  ...alignCenterCellStyle,
};

export const minorityPercentageColumnDefinition: ColumnDefinition = {
  headerName: 'Minority %',
  field: 'minorityPercentage',
  minWidth: 80,
  cellRenderer: PercentageOrRawRenderer,
  ...alignRightCellStyle,
};

export const importedMinorityPercentageColumnDefinition: ColumnDefinition = {
  headerName: 'Minority %',
  field: 'minorityPercentage',
  minWidth: 80,
  ...alignRightCellStyle,
};

export const ownershipColumnDefinition: ColumnDefinition = {
  headerName: 'Ownership',
  field: 'ownership',
  cellRenderer: OwnershipCellRenderer,
  ...alignRightCellStyle,
};

export const leUsTaxTypeColumnDefinition: ColumnDefinition = {
  headerName: 'US Tax Type',
  field: 'leUStaxType',
  ...alignCenterCellStyle,
};

export const leLocalTaxTypeColumnDefinition: ColumnDefinition = {
  headerName: 'Local Tax Type',
  field: 'leLocalTaxType',
  ...alignCenterCellStyle,
};

export const tbeCompanyCodeColumnDefinition: ColumnDefinition = {
  headerName: 'TBE Company Code',
  field: 'tbeCompanyCode',
  width: 150,
  minWidth: 135,
  ...alignLeftCellStyle,
};
export const tbeLegalEntityColumnDefinition: ColumnDefinition = {
  headerName: 'TBE Legal Entity',
  field: 'tbeLegalEntity',
  width: 140,
  minWidth: 125,
  ...alignCenterCellStyle,
};
export const tbeTypeColumnDefinition: ColumnDefinition = {
  headerName: 'TBE Type',
  field: 'tbeType',
  ...alignCenterCellStyle,
};

export const activeFlagColumnDefinition: ColumnDefinition = {
  headerName: 'Active Status',
  field: 'activeFlag',
  ...alignCenterCellStyle,
  ...excludeFromFilteringColumnProperty,
  minWidth: 100,
};

export const recordTypeColumnDefinition: ColumnDefinition = {
  headerName: 'Record Type',
  field: 'recordType',
  ...alignLeftCellStyle,
};

export const fromAccountColumnDefinition: ColumnDefinition = {
  headerName: 'From Account',
  field: 'fromAccount',
  ...alignRightCellStyle,
  minWidth: 200,
};

export const fromAccountDescriptionColumnDefintion: ColumnDefinition = {
  headerName: 'From Account Description',
  field: 'fromAccountDesc',
  ...alignLeftCellStyle,
  minWidth: 200,
};

export const toAccountColumnDefinition: ColumnDefinition = {
  headerName: 'To Account',
  field: 'toAccount',
  ...alignRightCellStyle,
};

export const emailColumnDefinition: ColumnDefinition = {
  headerName: 'Email',
  field: 'email',
  ...alignLeftCellStyle,
};

export const firstNameColumnDefinition: ColumnDefinition = {
  headerName: 'First Name',
  field: 'firstName',
  ...alignCenterCellStyle,
  minWidth: 100,
};

export const lastNameColumnDefinition: ColumnDefinition = {
  headerName: 'Last Name',
  field: 'lastName',
  ...alignCenterCellStyle,
  minWidth: 100,
};

export const lastLoginColumnDefinition: ColumnDefinition = {
  headerName: 'Last Login',
  field: 'lastLogin',
  cellRenderer: DateCellRenderer,
  ...alignCenterCellStyle,
  minWidth: 200,
};

export const ssoColumnDefinition: ColumnDefinition = {
  headerName: 'SSO',
  field: 'sso',
  minWidth: 85,
  ...alignCenterCellStyle,
};

export const createdOnColumnDefinition: ColumnDefinition = {
  headerName: 'Created On',
  field: 'createdOn',
  cellRenderer: DateCellRenderer,
  ...alignCenterCellStyle,
  minWidth: 125,
};

export const createdSsoColumnDefinition: ColumnDefinition = {
  headerName: 'Created SSO',
  field: 'createdSso',
  minWidth: 85,
  ...alignCenterCellStyle,
};

export const UpdatedSsoColumnDefinition: ColumnDefinition = {
  headerName: 'Updated SSO',
  field: 'updatedSso',
  minWidth: 85,
  ...alignCenterCellStyle,
};

export const roleColumnDefinition: ColumnDefinition = {
  headerName: 'Role',
  field: 'role',
  ...alignCenterCellStyle,
  cellRenderer: RoleCellRenderer,
};

export const userRoleColumnDefinition: ColumnDefinition = {
  headerName: 'Role',
  field: 'roleName',
  ...alignCenterCellStyle,
  maxWidth: 140,
  minWidth: 125,
};

export const clientColumnDefinition: ColumnDefinition = {
  headerName: 'Client',
  field: 'name',
  ...alignLeftCellStyle,
};

export const roleClientColumnDefinition: ColumnDefinition = {
  headerName: 'Client',
  field: 'clientName',
  ...alignLeftCellStyle,
};

export const clientsColumnDefinition: ColumnDefinition = {
  headerName: 'Client',
  field: 'clients',
  ...alignLeftCellStyle,
  minWidth: 300,
};

export const typeDescriptionColumnDefinition: ColumnDefinition = {
  headerName: 'Type Description',
  field: 'typeDesc',
  width: 160,
  minWidth: 140,
  ...alignLeftCellStyle,
};

export const allowTopsideColumnDefinition: ColumnDefinition = {
  headerName: 'Allow Topside',
  field: 'allowTopside',
  width: 80,
  minWidth: 60,
  ...alignCenterCellStyle,
};

export const activityDescriptionColumnDefinition: ColumnDefinition = {
  headerName: 'Activity Description',
  field: 'activityDesc',
  width: 160,
  minWidth: 140,
  ...alignLeftCellStyle,
};

export const slViewColumnDefinition: ColumnDefinition = {
  headerName: 'SL View',
  field: 'slView',
  width: 100,
  minWidth: 80,
  ...alignCenterCellStyle,
};

export const allowEntryColumnDefinition: ColumnDefinition = {
  headerName: 'Allow Entry',
  field: 'allowEntry',
  width: 80,
  minWidth: 60,
  ...alignCenterCellStyle,
};

export const allowTransferColumnDefinition: ColumnDefinition = {
  headerName: 'Allow Transfer',
  field: 'allowTransfer',
  width: 80,
  minWidth: 60,
  ...alignCenterCellStyle,
};

export const slGlJournalExistsColumnDefinition: ColumnDefinition = {
  headerName: 'SL GL Journal Exists',
  field: 'slGlJournalExists',
  width: 0,
  minWidth: 0,
  ...alignCenterCellStyle,
};

export const importResultsColumnDefinition: ColumnDefinition = {
  headerName: 'Results',
  field: 'importResults',
  ...alignLeftCellStyle,
};

export const assignmentTypeColumnDefinition: ColumnDefinition = {
  headerName: 'Assignment Type',
  field: 'assignmentType',
  minWidth: 200,
  suppressSizeToFit: true,
  ...alignCenterCellStyle,
};

export const assignmentColumnDefinition: ColumnDefinition = {
  headerName: 'Assignment',
  field: 'value',
  valueFormatter: specialTypeValueFormatter,
  ...alignLeftCellStyle,
};

export const assignmentsUsersColumnDefinition: ColumnDefinition = {
  headerName: 'Assignments',
  field: 'assignments',
  valueFormatter: specialTypeValueFormatter,
  ...alignLeftCellStyle,
};

export const countryDescriptionColumnDefinition: ColumnDefinition = {
  headerName: 'Country',
  field: 'countryDescription',
  suppressSizeToFit: true,
  ...alignLeftCellStyle,
};

export const companyCodeDescriptionColumnDefinition: ColumnDefinition = {
  headerName: 'Description',
  field: 'companyCodeDescription',
  minWidth: 200,
  ...alignLeftCellStyle,
};

export const allDescriptionColumnDefinition: ColumnDefinition = {
  headerName: '',
  field: 'allDescription',
  minWidth: 200,
  suppressSizeToFit: true,
};

export const slBalanceColumnDefinition: ColumnDefinition = {
  headerName: 'Balance',
  field: 'slBalance',
  minWidth: 100,
  cellRenderer: CustomRendererWithoutNull,
  enableValue: true,
  aggFunc: 'sum',
  ...alignRightCellStyle,
};

export const periodColumnDefinition: ColumnDefinition = {
  headerName: 'Period',
  field: 'periodName',
  minWidth: 120,
  ...alignCenterCellStyle,
  customExport: { mapValue: formatPeriodCell },
};

export const connNameColumnDefinition: ColumnDefinition = {
  headerName: 'Connection Name',
  field: 'connName',
  minWidth: 80,
  width: 100,
  ...alignCenterCellStyle,
};

export const tbeConnNameColumnDefinition: ColumnDefinition = {
  headerName: 'TBE Connection Name',
  field: 'tbeConnName',
  minWidth: 80,
  width: 100,
  ...alignCenterCellStyle,
};

export const clientIdColumnDefinition: ColumnDefinition = {
  headerName: 'Client ID',
  field: 'clientId',
  minWidth: 100,
  ...alignCenterCellStyle,
};

export const pwcClientIdColumnDefinition: ColumnDefinition = {
  headerName: 'PWC Client ID',
  field: 'pwcClientId',
  minWidth: 100,
  ...alignCenterCellStyle,
};

export const pwcEngagementIdColumnDefinition: ColumnDefinition = {
  headerName: 'PWC Engagement ID',
  field: 'pwcEngagementId',
  minWidth: 100,
  ...alignCenterCellStyle,
};

export const istClientIdColumnDefinition: ColumnDefinition = {
  headerName: 'IST Client ID',
  field: 'istClientId',
  minWidth: 100,
  ...alignCenterCellStyle,
};

export const cesClientPridColumnDefinition: ColumnDefinition = {
  headerName: 'CES Client PRID',
  field: 'cesClientPrid',
  minWidth: 100,
  ...alignCenterCellStyle,
};

export const startPeriodColumnDefinition: ColumnDefinition = {
  headerName: 'Start Period',
  field: 'startPeriod',
  minWidth: 100,
  ...alignCenterCellStyle,
  customExport: { mapValue: formatPeriodCell },
};

export const nameColumnDefinition: ColumnDefinition = {
  headerName: 'Name',
  field: 'name',
  minWidth: 150,
  ...alignLeftCellStyle,
};

export const shortNameColumnDefinition: ColumnDefinition = {
  headerName: 'Short Name',
  field: 'shortName',
  minWidth: 100,
  ...alignLeftCellStyle,
};

export const conversionRateSource: ColumnDefinition = {
  headerName: 'Conversion Rate Source',
  field: 'conversionRateSource',
  colId: 'conversionRateSource',
  minWidth: 100,
  ...alignCenterCellStyle,
};

export const reportingCurrency: ColumnDefinition = {
  headerName: 'Reporting Currency',
  field: 'reportingCurrency',
  colId: 'reportingCurrency',
  minWidth: 100,
  ...alignCenterCellStyle,
};

export const legalEntitiesSource: ColumnDefinition = {
  headerName: 'Legal Entities Source',
  field: 'legalEntitiesSource',
  colId: 'legalEntitiesSource',
  minWidth: 100,
  ...alignCenterCellStyle,
};

export const companyCodesSource: ColumnDefinition = {
  headerName: 'Company Codes Source',
  field: 'companyCodesSource',
  colId: 'companyCodesSource',
  minWidth: 100,
  ...alignCenterCellStyle,
};

export const accountsSource: ColumnDefinition = {
  headerName: 'Accounts Source',
  field: 'accountsSource',
  colId: 'accountsSource',
  minWidth: 100,
  ...alignCenterCellStyle,
};

export const businessUnitsSource: ColumnDefinition = {
  headerName: 'Business Units Source',
  field: 'businessUnitsSource',
  colId: 'businessUnitsSource',
  minWidth: 100,
  ...alignCenterCellStyle,
};

export const countryCodesSource: ColumnDefinition = {
  headerName: 'Country Codes Source',
  field: 'countryCodesSource',
  colId: 'countryCodesSource',
  minWidth: 140,
  ...alignLeftCellStyle,
  cellRenderer: CountryCodedOptionCellRenderer,
};

export const DBLogDetailColumnDefinition: ColumnDefinition = {
  headerName: 'DB Log Detail',
  field: 'DBLogDetail',
  colId: 'DBLogDetail',
  width: 80,
  minWidth: 80,
  ...alignCenterCellStyle,
};

export const lifecycleMapColumnDefinition: ColumnDefinition = {
  headerName: 'Lifecycle Map',
  field: 'lifecycleMap',
  ...alignLeftCellStyle,
};

export const connectionColumnDefinition: ColumnDefinition = {
  cellRenderer: ConnectionRunStatusLinkCellRenderer,
  headerName: 'Connection',
  field: 'connectionName',
  ...alignLeftCellStyle,
  minWidth: 100,
};

export const lastRunColumnDefinition: ColumnDefinition = {
  headerName: 'Last Run',
  field: 'lastRunDate',
  cellRenderer: DateCellRenderer,
  minWidth: 130,
};

export const plannedRunColumnDefinition: ColumnDefinition = {
  headerName: 'Planned Run',
  field: 'nextRunDate',
  cellRenderer: DateCellRenderer,
  minWidth: 100,
};

export const totalElapsedTimeColumnDefinition: ColumnDefinition = {
  headerName: 'Total Elapsed Time',
  field: 'runDuration',
  minWidth: 100,
};

export const runStatusColumnDefinition: ColumnDefinition = {
  headerName: 'Run Status',
  field: 'scheduleStatus',
  cellRenderer: RunStatusCellRenderer,
  minWidth: 100,
  filterParams: {
    suppressMiniFilter: true,
    cellRenderer: RunStatusFilterCellRenderer,
  },
};

export const runStatusIconColumnDefinition: ColumnDefinition = {
  headerName: 'Run Status',
  field: 'scheduleStatus',
  cellRenderer: RunStatusIconCellRenderer,
  minWidth: 40,
  filterParams: {
    suppressMiniFilter: true,
    cellRenderer: RunStatusFilterCellRenderer,
  },
};

export const heartbeatStatusColumnDefinition: ColumnDefinition = {
  cellRenderer: HeartbeatStatusCellRenderer,
  headerName: 'Heartbeat Status',
  field: 'heartbeatStatus',
  minWidth: 100,
  filterParams: {
    suppressMiniFilter: true,
    cellRenderer: HeartbeatStatusFilterCellRenderer,
  },
};

export const heartbeatStatusIconColumnDefinition: ColumnDefinition = {
  cellRenderer: HeartbeatStatusIconCellRenderer,
  headerName: 'Heartbeat Status',
  field: 'heartbeatStatus',
  minWidth: 60,
  filterParams: {
    suppressMiniFilter: true,
    cellRenderer: HeartbeatStatusFilterCellRenderer,
  },
};

export const proportionOfTaxColumnDefinition: ColumnDefinition = {
  headerName: 'Proportion of Tax',
  valueFormatter: PercentageValueFormatter,
  field: 'proportionOfTax',
  width: 110,
  minWidth: 100,
  ...alignRightCellStyle,
};

export const summaryFlagColumnDefinition: ColumnDefinition = {
  headerName: 'Summary Flag',
  field: 'summaryFlag',
  width: 80,
  minWidth: 60,
  ...excludeFromFilteringColumnProperty,
};

export const allowAccountBasedAdjustmentsColumnDefinition: ColumnDefinition = {
  headerName: 'Allow Account Based Adjustments',
  field: 'allowAdjFlag',
  minWidth: 160,
  ...excludeFromFilteringColumnProperty,
};

export const usedByColumnDefinition: ColumnDefinition = {
  headerName: 'Used By',
  field: 'usedBy',
  width: 100,
  minWidth: 80,
  ...alignLeftCellStyle,
};

export const parsedColumnDefinition: ColumnDefinition = {
  headerName: 'Parsed',
  field: 'parsed',
  width: 80,
  minWidth: 60,
  ...alignLeftCellStyle,
};

export const beginingBalanceDebitColumnDefinition: ColumnDefinition = {
  headerName: 'Begin Balance Debit',
  field: 'beginBalanceDebit',
  width: 200,
  minWidth: 80,
  cellRenderer: CustomDecimalRenderer,
  ...alignRightCellStyle,
};

export const beginingBalanceCreditColumnDefinition: ColumnDefinition = {
  headerName: 'Begin Balance Credit',
  field: 'beginBalanceCredit',
  width: 200,
  minWidth: 80,
  cellRenderer: CustomDecimalRenderer,
  ...alignRightCellStyle,
};

export const beginingGrossBalanceColumnDefinition: ColumnDefinition = {
  headerName: 'Begin Gross Balance',
  field: 'beginGrossBalance',
  width: 200,
  minWidth: 80,
  cellRenderer: CustomDecimalRenderer,
  ...alignRightCellStyle,
};

export const periodNetDebitColumnDefinition: ColumnDefinition = {
  headerName: 'Period Net Debit',
  field: 'periodNetDebit',
  width: 200,
  minWidth: 80,
  cellRenderer: CustomDecimalRenderer,
  ...alignRightCellStyle,
};

export const periodNetCreditColumnDefinition: ColumnDefinition = {
  headerName: 'Period Net Credit',
  field: 'periodNetCredit',
  width: 200,
  minWidth: 80,
  cellRenderer: CustomDecimalRenderer,
  ...alignRightCellStyle,
};

export const periodGrossAmountColumnDefinition: ColumnDefinition = {
  headerName: 'Period Gross Amount',
  field: 'periodGrossAmount',
  width: 200,
  minWidth: 80,
  cellRenderer: CustomDecimalRenderer,
  ...alignRightCellStyle,
};

export const periodGrossAmountUnapprovedColumnDefinition: ColumnDefinition = {
  headerName: 'Period Gross Amount Unapproved',
  field: 'periodGrossAmountUnapproved',
  width: 200,
  minWidth: 80,
  cellRenderer: CustomDecimalRenderer,
  ...alignRightCellStyle,
};

export const tbBalanceColumnDefinition: ColumnDefinition = {
  headerName: 'TB Balance',
  field: 'tbBalance',
  width: 200,
  minWidth: 80,
  cellRenderer: CustomDecimalRenderer,
  ...alignRightCellStyle,
};

export const tbCurrencyCodeColumnDefinition: ColumnDefinition = {
  headerName: 'TB Currency Code',
  field: 'tbCurrencyCode',
  width: 200,
  minWidth: 80,
  ...alignCenterCellStyle,
};

export const tbBalanceMorColumnDefinition: ColumnDefinition = {
  headerName: 'TB Balance MOR',
  field: 'tbBalanceMor',
  width: 100,
  minWidth: 80,
  cellRenderer: CustomDecimalRenderer,
  ...alignRightCellStyle,
};

export const tbCurrencyCodeMorColumnDefinition: ColumnDefinition = {
  headerName: 'TB Currency Code MOR',
  field: 'tbCurrencyCodeMor',
  width: 200,
  minWidth: 80,
  ...alignCenterCellStyle,
};

export const diffBalanceColumnDefinition: ColumnDefinition = {
  headerName: 'Difference Balance',
  field: 'diffBalance',
  width: 200,
  minWidth: 80,
  cellRenderer: CustomDecimalRenderer,
  ...alignRightCellStyle,
};

export const diffCurrencyCodeColumnDefinition: ColumnDefinition = {
  headerName: 'Difference Currency Code',
  field: 'diffCurrencyCode',
  width: 200,
  minWidth: 80,
  ...alignCenterCellStyle,
};

export const tagNameColumnDefinition: ColumnDefinition = {
  headerName: 'Tag Name',
  field: 'tagName',
  width: 120,
  minWidth: 100,
  ...alignLeftCellStyle,
};

export const subTagColumnDefinition: ColumnDefinition = {
  headerName: 'Sub Tag',
  field: 'subTagDesc',
  width: 120,
  minWidth: 100,
  ...alignLeftCellStyle,
};

export const catViewColumnDefinition: ColumnDefinition = {
  headerName: 'Cat View',
  field: 'catView',
  width: 120,
  minWidth: 100,
  ...alignLeftCellStyle,
};

export const defaultTagColumnDefiniton: ColumnDefinition = {
  headerName: 'Default Tag',
  field: 'defaultTag',
  width: 120,
  minWidth: 100,
};

export const percentageColumnDefinition: ColumnDefinition = {
  headerName: 'Percentage',
  field: 'percentage',
  width: 100,
  valueFormatter: PercentageValueFormatter,
  minWidth: 100,
  ...alignRightCellStyle,
};

export const deferredPlAccountColumnDefinition: ColumnDefinition = {
  headerName: 'Deferred P&L',
  field: 'defPlAccount',
  ...alignCenterCellStyle,
  minWidth: 100,
};

export const deferredBsAccountColumnDefinition: ColumnDefinition = {
  headerName: 'Deferred BS',
  field: 'defBsAccount',
  ...alignCenterCellStyle,
  minWidth: 100,
};

export const reportIdColumnDefinition: ColumnDefinition = {
  headerName: 'Report ID',
  field: 'reportSetId',
  width: 220,
  minWidth: 200,
  ...alignCenterCellStyle,
};

export const reportNameColumnDefinition: ColumnDefinition = {
  headerName: 'Report Name',
  field: 'reportSetName',
  width: 400,
  minWidth: 200,
  ...alignLeftCellStyle,
};

export const customReportTypeColumnDefinition: ColumnDefinition = {
  headerName: 'Report Type',
  field: 'customReportType',
  ...alignLeftCellStyle,
};

export const deferredAccountColumnDefinition: ColumnDefinition = {
  headerName: 'Deferred Account',
  field: 'defAccount',
  ...alignLeftCellStyle,
  minWidth: 100,
};

export const deferredGroupingColumnDefinition: ColumnDefinition = {
  headerName: 'Deferred Grouping',
  field: 'defGrouping',
  ...alignLeftCellStyle,
  minWidth: 100,
};

export const grossBalanceColumnDefinition: ColumnDefinition = {
  headerName: 'Gross Adjusted Beginning Balance',
  field: 'grossBalance',
  ...alignRightCellStyle,
  cellRenderer: CustomDecimalRenderer,
  minWidth: 130,
};

export const historicalRateColumnDefinition: ColumnDefinition = {
  headerName: 'Historical Rate',
  field: 'historicalRate',
  ...alignRightCellStyle,
  valueFormatter: PercentageValueFormatter,
  minWidth: 100,
};

export const netBalanceColumnDefinition: ColumnDefinition = {
  headerName: 'Net Balance',
  field: 'netBalance',
  ...alignRightCellStyle,
  cellRenderer: CustomDecimalRenderer,
  minWidth: 100,
};

export const newRateColumnDefinition: ColumnDefinition = {
  headerName: 'New Rate',
  field: 'newRate',
  ...alignRightCellStyle,
  cellRenderer: PercentageOrRawRenderer,
  minWidth: 100,
};

export const adjustedNetBalanceColumnDefinition: ColumnDefinition = {
  headerName: 'Adjusted Net Balance',
  field: 'adjustedNetBalance',
  ...alignRightCellStyle,
  cellRenderer: CustomDecimalRenderer,
  minWidth: 100,
};

export const rateChangeEntryColumnDefinition: ColumnDefinition = {
  headerName: 'Rate Change Entry',
  field: 'rateChangeEntry',
  ...alignRightCellStyle,
  cellRenderer: CustomDecimalRenderer,
  minWidth: 100,
};

export const commentColumnDefinition: ColumnDefinition = {
  headerName: 'Comment',
  field: 'comment',
  editable: true,
  ...alignLeftCellStyle,
  minWidth: 200,
};

export const commentsColumnDefinition: ColumnDefinition = {
  headerName: 'Comment',
  field: 'comments',
  ...alignLeftCellStyle,
  minWidth: 200,
};

export const addUpdateColumn: ColumnDefinition = {
  lockPosition: true,
  headerName: 'Add/Update',
  field: 'addOrUpdate',
  minWidth: 120,
  maxWidth: 120,
  comparator: compareAddOrUpdate,
  ...alignLeftCellStyle,
};

export const countryTaxTypeDescriptionColumnDefinition: ColumnDefinition = {
  headerName: 'Country Tax Type Description',
  field: 'countryTaxTypeDescription',
  minWidth: 200,
  ...alignLeftCellStyle,
};

export const stateMinimumTaxColumDefinition: ColumnDefinition = {
  headerName: 'State Minimum Tax',
  field: 'stateMinimumTax',
  minWidth: 120,
  ...alignCenterCellStyle,
};

export const stateApportionmentRateColumnDefinition: ColumnDefinition = {
  headerName: 'State Apportionment Rate',
  field: 'stateApportionmentRate',
  minWidth: 140,
  valueFormatter: PercentageOrRawValueFormatter as ColumnDefinition['valueFormatter'],
  filterParams: {
    valueFormatter: PercentageOrRawValueFormatter,
  },
  customExport: {
    omitIfNull: true,
  },
  ...alignRightCellStyle,
};

export const statePrePostNOLColumnDefinition: ColumnDefinition = {
  headerName: 'State Pre/Post NOL',
  field: 'statePrePostNOL',
  minWidth: 120,
  ...alignCenterCellStyle,
};

export const federalFTCLimitColumnDefinition: ColumnDefinition = {
  headerName: 'Federal FTC Limit',
  field: 'federalFTCLimit',
  minWidth: 120,
  ...alignCenterCellStyle,
};

export const NOLColumnDefinition: ColumnDefinition = {
  headerName: 'NOL',
  field: 'NOL',
  minWidth: 120,
  ...alignCenterCellStyle,
};

export const recordVAColumnDefinition: ColumnDefinition = {
  headerName: 'Record VA',
  field: 'recordVA',
  minWidth: 120,
  ...alignCenterCellStyle,
};

export const fromCurrencyColumnDefinition: ColumnDefinition = {
  headerName: 'From Currency',
  field: 'fromCurrency',
  minWidth: 120,
  ...alignCenterCellStyle,
};

export const toCurrencyColumnDefinition: ColumnDefinition = {
  headerName: 'To Currency',
  field: 'toCurrency',
  minWidth: 120,
  ...alignCenterCellStyle,
};

export const openSpotRateColumnDefinition: ColumnDefinition = {
  headerName: 'Open Spot Rate',
  field: 'openSpotRate',
  minWidth: 120,
  cellRenderer: CurrencyRateCellRenderer,
  ...alignCenterCellStyle,
};

export const endSpotRateColumnDefinition: ColumnDefinition = {
  headerName: 'End Spot Rate',
  field: 'endSpotRate',
  minWidth: 120,
  cellRenderer: CurrencyRateCellRenderer,
  ...alignCenterCellStyle,
};

export const monthlyOperatingRateColumnDefinition: ColumnDefinition = {
  headerName: 'Monthly Operating Rate',
  field: 'monthlyOperatingRate',
  minWidth: 120,
  cellRenderer: CurrencyRateCellRenderer,
  ...alignCenterCellStyle,
};

export const weightedAverageRateColumnDefinition: ColumnDefinition = {
  headerName: 'Weighted Average Rate',
  field: 'weightedAverageRate',
  minWidth: 120,
  cellRenderer: CurrencyRateCellRenderer,
  ...alignCenterCellStyle,
};

export const fromCurrencyPrecisionColumnDefinition: ColumnDefinition = {
  headerName: 'From Currency Precision',
  field: 'fromCurrencyPrecision',
  minWidth: 120,
  ...alignCenterCellStyle,
};

export const toCurrencyPrecisionColumnDefinition: ColumnDefinition = {
  headerName: 'To Currency Precision',
  field: 'toCurrencyPrecision',
  minWidth: 120,
  ...alignCenterCellStyle,
};

export const lastUpdateDateColumnDefinition: ColumnDefinition = {
  headerName: 'Last Update Date',
  field: 'updatedOn',
  minWidth: 120,
  cellRenderer: DateCellRenderer,
  ...alignCenterCellStyle,
};

export const preTaxColumnDefinition: ColumnDefinition = {
  headerName: 'Pre Tax',
  field: 'preTaxFlag',
  colId: 'preTax',
  minWidth: 80,
  ...alignCenterCellStyle,
};

export const trialBalanceThresholdColumnDefinition: ColumnDefinition = {
  headerName: 'Trial Balance Out of Balance Threshold',
  field: 'trialBalanceThreshold',
  minWidth: 120,
  ...alignCenterCellStyle,
};

export const deferredGroupColumnDefinition: ColumnDefinition = {
  headerName: 'Deferred Group',
  field: 'DEF_GRP',
  minWidth: 180,
  ...alignLeftCellStyle,
};

export const etrGroupColumnDefinition: ColumnDefinition = {
  headerName: 'ETR Group',
  field: 'ETR_GRP',
  minWidth: 180,
  ...alignLeftCellStyle,
};

export const emailDomainColumnDefinition: ColumnDefinition = {
  headerName: 'Email Domains',
  field: 'emailDomain',
  minWidth: 150,
  maxWidth: 160,
  ...alignLeftCellStyle,
  valueFormatter: params => {
    const value = params.value || '';
    return value.length > 20 ? `${value.substring(0, 20)}...` : value;
  },
};

export const supportEmailColumnDefinition: ColumnDefinition = {
  headerName: 'Support Email',
  field: 'supportEmail',
  minWidth: 180,
  ...alignLeftCellStyle,
};

export const notificationEmailColumnDefinition: ColumnDefinition = {
  headerName: 'Notification Email',
  field: 'notificationEmail',
  minWidth: 180,
  ...alignLeftCellStyle,
};

export const reportFolderColumnDefinition: ColumnDefinition = {
  headerName: 'Report Folder',
  field: 'reportFolder',
  minWidth: 180,
  ...alignLeftCellStyle,
};

export const currentTaxRateColumnDefinition: ColumnDefinition = {
  headerName: 'Current Tax Rate',
  field: 'currentTaxRate',
  valueFormatter: PercentageValueFormatter,
  filterParams: {
    valueFormatter: PercentageValueFormatter,
  },
  minWidth: 80,
};

export const deferredRateColumnDefinition: ColumnDefinition = {
  headerName: 'Deferred Rate',
  field: 'deferredRate',
  valueFormatter: PercentageValueFormatter,
  minWidth: 80,
};

export const cyEffectiveTaxRateColumnDefinition: ColumnDefinition = {
  headerName: 'CY Effective Tax Rate',
  field: 'cyEffectiveTaxRate',
  valueFormatter: PercentageValueFormatter,
  minWidth: 80,
};

export const effectiveTaxRateColumnDefinition: ColumnDefinition = {
  headerName: 'Effective Tax Rate',
  field: 'effectiveTaxRate',
  valueFormatter: PercentageValueFormatter,
  minWidth: 80,
};

export const apportionmentRateColumnDefinition: ColumnDefinition = {
  headerName: 'Apportionment Rate',
  field: 'apportionmentRate',
  valueFormatter: PercentageValueFormatter,
  minWidth: 80,
};

export const statEffectiveTaxRateColumnDefinition: ColumnDefinition = {
  headerName: 'STAT Effective Tax Rate',
  field: 'statEffectiveTaxRate',
  valueFormatter: PercentageValueFormatter,
  minWidth: 80,
};

export const gaapEffectiveTaxRateColumnDefinition: ColumnDefinition = {
  headerName: 'GAAP Effective Tax Rate',
  field: 'gaapEffectiveTaxRate',
  valueFormatter: PercentageValueFormatter,
  minWidth: 80,
};

export const secondaryTaxColumnDefinition: ColumnDefinition = {
  field: 'secondaryTax',
  headerName: 'Secondary Tax',
  width: 80,
  minWidth: 80,
};

export const alertMessageColumnDefinition: ColumnDefinition = {
  headerName: 'Alert Message',
  field: 'alertMessage',
  colId: 'alertMessage',
  minWidth: 180,
  ...alignLeftCellStyle,
};

export const alertExpireOnColumnDefinition: ColumnDefinition = {
  headerName: 'Alert Expire On',
  field: 'alertExpireOn',
  colId: 'alertExpireOn',
  minWidth: 180,
  ...alignLeftCellStyle,
};

export const connectionsColumnDefinition: ColumnDefinition = {
  headerName: 'Connections',
  field: 'connections',
  colId: 'connections',
  cellRenderer: StringsArrayCellRenderer,
  width: 120,
  minWidth: 120,
  getQuickFilterText: getQuickFilterTextForStringArrayColumn,
  ...alignLeftCellStyle,
};

export const businessUnitsColumnDefinition: ColumnDefinition = {
  headerName: 'Business Units',
  field: 'businessUnits',
  colId: 'businessUnits',
  cellRenderer: StringsArrayCellRenderer,
  width: 120,
  minWidth: 120,
  getQuickFilterText: getQuickFilterTextForStringArrayColumn,
  ...alignLeftCellStyle,
};

export const excludedCompanyCodesColumnDefinition: ColumnDefinition = {
  headerName: 'Exclude Company Codes',
  field: 'excludedCompanyCodes',
  colId: 'excludedCompanyCodes',
  cellRenderer: StringsArrayCellRenderer,
  width: 140,
  minWidth: 140,
  getQuickFilterText: getQuickFilterTextForStringArrayColumn,
  ...alignLeftCellStyle,
};

export const includeAllApplicableCountryTaxTypesColumnDefinition: ColumnDefinition = {
  headerName: 'All Applicable Country Tax Types',
  field: 'includeAllApplicableCountryTaxTypes',
  colId: 'includeAllApplicableCountryTaxTypes',
  minWidth: 150,
  width: 150,
  valueGetter: ({ data }) => data.countryTaxTypes && data.countryTaxTypes[0] === ALL_OPTION ? YNOptions.Y : YNOptions.N,
  ...alignCenterCellStyle,
};

export const countryTaxTypesColumnDefinition: ColumnDefinition = {
  headerName: 'Country Tax Types',
  field: 'countryTaxTypes',
  minWidth: 100,
  width: 500,
  cellRenderer: StringsArrayCellRenderer,
  getQuickFilterText: getQuickFilterTextForStringArrayColumn,
  ...alignLeftCellStyle,
};

export const bodyTextColumnDefinition: ColumnDefinition = {
  headerName: 'Body Text',
  field: 'bodyText',
  minWidth: 200,
  ...alignLeftCellStyle,
};

export const titleColumnDefinition: ColumnDefinition = {
  headerName: 'Title',
  field: 'title',
  width: 100,
  ...alignLeftCellStyle,
};

export const nameOverrideColumnDefinition: ColumnDefinition = {
  headerName: 'Name Override',
  field: 'nameOverride',
  width: 100,
  ...alignLeftCellStyle,
};

export const fiscalStartMonthDefinition: ColumnDefinition = {
  headerName: 'Fiscal Start Month',
  field: 'fiscalStartMonth',
  colId: 'fiscalStartMonth',
  width: 40,
  minWidth: 40,
  ...alignCenterCellStyle,
};

export const startDateColumnDefinition: ColumnDefinition = {
  headerName: 'Start Date',
  field: 'startDate',
  colId: 'startDate',
  cellRenderer: DateCellRenderer,
  width: 140,
  minWidth: 140,
  ...alignCenterCellStyle,
};

export const endDateColumnDefinition: ColumnDefinition = {
  headerName: 'End Date',
  field: 'endDate',
  colId: 'endDate',
  cellRenderer: DateCellRenderer,
  width: 140,
  minWidth: 140,
  ...alignCenterCellStyle,
};

export const yearStartDateColumnDefinition: ColumnDefinition = {
  headerName: 'Year Start Date',
  field: 'yearStartDate',
  colId: 'yearStartDate',
  cellRenderer: DateCellRenderer,
  width: 140,
  minWidth: 140,
  ...alignCenterCellStyle,
};

export const quarterStartDateColumnDefinition: ColumnDefinition = {
  headerName: 'Quarter Start Date',
  field: 'quarterStartDate',
  colId: 'quarterStartDate',
  cellRenderer: DateCellRenderer,
  width: 140,
  minWidth: 140,
  ...alignCenterCellStyle,
};

export const periodYearColumnDefinition: ColumnDefinition = {
  headerName: 'Period Year',
  field: 'periodYear',
  colId: 'periodYear',
  width: 80,
  minWidth: 80,
  ...alignCenterCellStyle,
};

export const periodNumberColumnDefinition: ColumnDefinition = {
  headerName: 'Period Number',
  field: 'periodNum',
  colId: 'periodNum',
  width: 40,
  minWidth: 40,
  ...alignCenterCellStyle,
};

export const quarterNumberColumnDefinition: ColumnDefinition = {
  headerName: 'Quarter Number',
  field: 'quarterNum',
  colId: 'quarterNum',
  width: 40,
  minWidth: 40,
  ...alignCenterCellStyle,
};

export const adjustmentPeriodFlag: ColumnDefinition = {
  headerName: 'Adjustment Period Flag',
  field: 'adjustmentPeriodFlag',
  colId: 'adjustmentPeriodFlag',
  width: 70,
  minWidth: 70,
  ...alignCenterCellStyle,
};

export const reviewedDateColumnDefinition: ColumnDefinition = {
  headerName: 'Reviewed Date',
  field: 'reviewedDate',
  cellRenderer: DateCellRenderer,
  width: 140,
  minWidth: 125,
};

export const calculationRunColumnDefinition: ColumnDefinition = {
  headerName: 'Calculation Run',
  field: 'calculationRun',
  cellRenderer: DateCellRenderer,
  width: 140,
  minWidth: 125,
};

export const selectRowCheckboxColumnDefinition: ColumnDefinition = {
  cellRenderer: CheckboxCellRenderer(),
  cellRendererParams: ({ node, api }: any) => ({
    isChecked: () => node.isSelected(),
    onSelect: () => {
      node.setSelected(!node.isSelected());
      api.refreshCells({ force: true, rowNodes: [node] });
    },
  }),
  width: 40,
  minWidth: 40,
  ...alignCenterCellStyle,
};

export const userCreatedDateDefinition: ColumnDefinition = {
  headerName: 'User Created Date',
  field: 'userCreatedDate',
  cellRenderer: DateCellRenderer,
  width: 140,
  minWidth: 125,
};

export const userLastLoginDefinition: ColumnDefinition = {
  headerName: 'User Last Login',
  field: 'userLastLogin',
  cellRenderer: DateCellRenderer,
  width: 140,
  minWidth: 125,
};

export const privilegeCreatedDateDefinition: ColumnDefinition = {
  headerName: 'Privilege Created Date',
  field: 'privilegeCreatedDate',
  cellRenderer: DateCellRenderer,
  width: 140,
  minWidth: 125,
};

export const clientStatusColumnDefinition: ColumnDefinition = {
  headerName: 'Status',
  field: 'clientStatus',
  ...alignLeftCellStyle,
  maxWidth: 140,
  minWidth: 125,
};

export const privilegesColumnDefinition: ColumnDefinition = {
  headerName: 'Privileges',
  field: 'privileges',
  ...alignLeftCellStyle,
  maxWidth: 140,
  minWidth: 125,
};

export const bttConnColumnDefinition: ColumnDefinition = {
  headerName: 'Connections',
  field: 'conn',
  ...alignLeftCellStyle,
  maxWidth: 140,
  minWidth: 125,
};

export const bttBUColumnDefinition: ColumnDefinition = {
  headerName: 'Business Units',
  field: 'bu',
  ...alignLeftCellStyle,
  maxWidth: 140,
  minWidth: 125,
};

export const bttCTTColumnDefinition: ColumnDefinition = {
  headerName: 'Country Tax Types',
  field: 'ctt',
  ...alignLeftCellStyle,
  maxWidth: 140,
  minWidth: 125,
};

export const bsmCcNameColumnDefinition: ColumnDefinition = {
  headerName: 'Company Name',
  field: 'ccName',
  ...alignLeftCellStyle,
  maxWidth: 140,
  minWidth: 125,
};

export const bsmBuDescColumnDefinition: ColumnDefinition = {
  headerName: 'BU Name',
  field: 'buDescription',
  ...alignLeftCellStyle,
  maxWidth: 140,
  minWidth: 125,
};

export const bsmCttsColumnDefinition: ColumnDefinition = {
  headerName: 'Country Tax Type',
  field: 'countryTaxType',
  ...alignLeftCellStyle,
  maxWidth: 140,
  minWidth: 125,
};

export const bsmDefRateChangeColumnDefinition: ColumnDefinition = {
  headerName: 'Rate Change',
  field: 'defRateChg',
  ...alignLeftCellStyle,
  maxWidth: 140,
  minWidth: 125,
};

export const bsmDefRtaColumnDefinition: ColumnDefinition = {
  headerName: 'Return to Accrual',
  field: 'defRta',
  ...alignLeftCellStyle,
  maxWidth: 140,
  minWidth: 125,
};

export const bsmDefAuditTrueUpColumnDefinition: ColumnDefinition = {
  headerName: 'Audit True up',
  field: 'defAauditTtrueUp',
  ...alignLeftCellStyle,
  maxWidth: 140,
  minWidth: 125,
};

export const bsmDefDtaDtlAdjColumnDefinition: ColumnDefinition = {
  headerName: 'DTA/(DTL) Adjustment',
  field: 'defDtAdj',
  ...alignLeftCellStyle,
  maxWidth: 140,
  minWidth: 125,
};

export const bsmDefPraColumnDefinition: ColumnDefinition = {
  headerName: 'Post Return Adjustment',
  field: 'defPra',
  ...alignLeftCellStyle,
  maxWidth: 140,
  minWidth: 125,
};

export const bsmDefBalanceSOColumnDefinition: ColumnDefinition = {
  headerName: 'Balance Sheet Other',
  field: 'defBsOth',
  ...alignLeftCellStyle,
  maxWidth: 140,
  minWidth: 125,
};

export const bsmAdjBenBalColumnDefinition: ColumnDefinition = {
  headerName: 'Adjusted Beginning Balance',
  field: 'adjBeginBalance',
  ...alignLeftCellStyle,
  maxWidth: 140,
  minWidth: 125,
};

export const bsmCyActivityColumnDefinition: ColumnDefinition = {
  headerName: 'CY Activity',
  field: 'defProv',
  ...alignLeftCellStyle,
  maxWidth: 140,
  minWidth: 125,
};

export const bsmReclassColumnDefinition: ColumnDefinition = {
  headerName: 'Reclass',
  field: 'defReclass',
  ...alignLeftCellStyle,
  maxWidth: 140,
  minWidth: 125,
};

export const bsmEquityColumnDefinition: ColumnDefinition = {
  headerName: 'Equity',
  field: 'defEquity',
  ...alignLeftCellStyle,
  maxWidth: 140,
  minWidth: 125,
};

export const bsmGoodwillColumnDefinition: ColumnDefinition = {
  headerName: 'Goodwill',
  field: 'defGoodwill',
  ...alignLeftCellStyle,
  maxWidth: 140,
  minWidth: 125,
};

export const bsmOciColumnDefinition: ColumnDefinition = {
  headerName: 'Other Comprehensive Income',
  field: 'defOci',
  ...alignLeftCellStyle,
  maxWidth: 140,
  minWidth: 125,
};

export const bsmEndBalColumnDefinition: ColumnDefinition = {
  headerName: 'Ending Balance',
  field: 'defEndBalance',
  ...alignLeftCellStyle,
  maxWidth: 140,
  minWidth: 125,
};

export const bsmNetAmountColumnDefinition: ColumnDefinition = {
  headerName: 'Net Amount',
  field: 'netAmount',
  ...alignLeftCellStyle,
  maxWidth: 140,
  minWidth: 125,
};

export const bsmPreparerNameColumnDefinition: ColumnDefinition = {
  headerName: 'Preparer',
  field: 'preparer',
  ...alignLeftCellStyle,
  maxWidth: 140,
  minWidth: 125,
};

export const bsmReviewerNameColumnDefinition: ColumnDefinition = {
  headerName: 'Reviewer',
  field: 'reviewer',
  ...alignLeftCellStyle,
  maxWidth: 140,
  minWidth: 125,
};

export const bsmCatNameColumnDefinition: ColumnDefinition = {
  headerName: 'Tax Entry Type',
  field: 'categoryName',
  ...alignLeftCellStyle,
  maxWidth: 140,
  minWidth: 125,
};

export const bsmOpenColumnDefinition: ColumnDefinition = {
  headerName: 'Opening',
  field: 'open',
  ...alignLeftCellStyle,
  maxWidth: 140,
  minWidth: 125,
};

export const bsmPymtColumnDefinition: ColumnDefinition = {
  headerName: 'Payments',
  field: 'pymt',
  ...alignLeftCellStyle,
  maxWidth: 140,
  minWidth: 125,
};

export const bsmTranColumnDefinition: ColumnDefinition = {
  headerName: 'Transfers',
  field: 'tran',
  ...alignLeftCellStyle,
  maxWidth: 140,
  minWidth: 125,
};

export const bsmOthColumnDefinition: ColumnDefinition = {
  headerName: 'Other',
  field: 'oth',
  ...alignLeftCellStyle,
  maxWidth: 140,
  minWidth: 125,
};

export const bsmEndColumnDefinition: ColumnDefinition = {
  headerName: 'Ending',
  field: 'end',
  ...alignLeftCellStyle,
  maxWidth: 140,
  minWidth: 125,
};

export const bsmPeriodNetAmountUnapprovedColumnDefinition: ColumnDefinition = {
  headerName: 'Period Net Unapproved',
  field: 'periodNetAmountUnapproved',
  ...alignLeftCellStyle,
  maxWidth: 140,
  minWidth: 125,
};

export const bsmBeginNetBalanceColumnDefinition: ColumnDefinition = {
  headerName: 'Begin Net',
  field: 'beginNetBalance',
  ...alignLeftCellStyle,
  maxWidth: 140,
  minWidth: 125,
};

export const ccSlFlagColumnDefinition: ColumnDefinition = {
  headerName: 'BSM Flag',
  field: 'slflag',
  ...alignLeftCellStyle,
  maxWidth: 140,
  minWidth: 125,
};

export const deleteAcctColumnDefinition: ColumnDefinition = {
  headerName: 'Deleted Account',
  field: 'deletedAccount',
  ...alignLeftCellStyle,
  maxWidth: 140,
  minWidth: 125,
};

export const gaapNonMultiIbtColumnDefinition: ColumnDefinition = {
  headerName: 'GAAP Income/(Loss) Before Tax',
  field: 'gaapIbt',
  ...alignLeftCellStyle,
  maxWidth: 140,
  minWidth: 125,
};

export const gaapNonMultiPermColumnDefinition: ColumnDefinition = {
  headerName: 'G2S Non-Reversing Differences',
  field: 'g2sPerm',
  ...alignLeftCellStyle,
  maxWidth: 140,
  minWidth: 125,
};

export const gaapNonMultiTempColumnDefinition: ColumnDefinition = {
  headerName: 'G2S Reversing Differences',
  field: 'g2sTemp',
  ...alignLeftCellStyle,
  maxWidth: 140,
  minWidth: 125,
};

export const gaapNonMultiStatIbtColumnDefinition: ColumnDefinition = {
  headerName: 'Stat Income/(Loss) Before Tax',
  field: 'statIbt',
  ...alignLeftCellStyle,
  maxWidth: 140,
  minWidth: 125,
};

export const gaapNonMultiB2tPermColumnDefinition: ColumnDefinition = {
  headerName: 'Tax Permanent Differences',
  field: 'b2tPerm',
  ...alignLeftCellStyle,
  maxWidth: 140,
  minWidth: 125,
};

export const gaapNonMultiB2tTempColumnDefinition: ColumnDefinition = {
  headerName: 'Tax Temporary Differences',
  field: 'b2tTemp',
  ...alignLeftCellStyle,
  maxWidth: 140,
  minWidth: 125,
};

export const gaapNonMultiTaxIncLossColumnDefinition: ColumnDefinition = {
  headerName: 'Taxable Income/(Loss)',
  field: 'txblInc',
  ...alignLeftCellStyle,
  maxWidth: 140,
  minWidth: 125,
};

export const gaapNonMultiUtilizePYNolColumnDefinition: ColumnDefinition = {
  headerName: 'Utilize Prior Year NOLS',
  field: 'utilizePyNol',
  ...alignLeftCellStyle,
  maxWidth: 140,
  minWidth: 125,
};

export const gaapNonMultiTxblIncAftNolColumnDefinition: ColumnDefinition = {
  headerName: 'Taxable Income After NOL Util',
  field: 'txblIncAftNol',
  ...alignLeftCellStyle,
  maxWidth: 140,
  minWidth: 125,
};

export const gaapNonMultiRecordCyNolColumnDefinition: ColumnDefinition = {
  headerName: 'Record Current Year NOL',
  field: 'recordCyNol',
  ...alignLeftCellStyle,
  maxWidth: 140,
  minWidth: 125,
};

export const gaapNonMultiCurTaxB4CrColumnDefinition: ColumnDefinition = {
  headerName: 'GAAP Cur Tax Exp Ben B4 Credit',
  field: 'gaapCurTaxB4Cr',
  ...alignLeftCellStyle,
  maxWidth: 140,
  minWidth: 125,
};

export const gaapNonMultiTotalCrColumnDefinition: ColumnDefinition = {
  headerName: 'Total Credits and Other After Tax Adj',
  field: 'totalCr',
  ...alignLeftCellStyle,
  maxWidth: 140,
  minWidth: 125,
};

export const gaapNonMultiB2tTempAdjColumnDefinition: ColumnDefinition = {
  headerName: 'Temporary Book to Tax Adj',
  field: 'b2tTempAdj',
  ...alignLeftCellStyle,
  maxWidth: 140,
  minWidth: 125,
};

export const gaapNonMultiG2sDefTaxPColumnDefinition: ColumnDefinition = {
  headerName: 'G2S Deferred Tax (P)',
  field: 'g2sDefTaxP',
  ...alignLeftCellStyle,
  maxWidth: 140,
  minWidth: 125,
};

export const gaapNonMultiGaapG2sDefTaxGColumnDefinition: ColumnDefinition = {
  headerName: 'G2S Deferred Tax (G)',
  field: 'gaapG2sDefTaxG',
  ...alignLeftCellStyle,
  maxWidth: 140,
  minWidth: 125,
};

export const gaapNonMultiNoNolColumnDefinition: ColumnDefinition = {
  headerName: 'Net Operating NOL',
  field: 'noNol',
  ...alignLeftCellStyle,
  maxWidth: 140,
  minWidth: 125,
};

export const gaapNonMultiGaapRecordVaColumnDefinition: ColumnDefinition = {
  headerName: 'Record Valuation Allowance',
  field: 'gaapRecordVa',
  ...alignLeftCellStyle,
  maxWidth: 140,
  minWidth: 125,
};

export const gaapNonMultiGaapTotDefColumnDefinition: ColumnDefinition = {
  headerName: 'Total Deferreds',
  field: 'gaapTotDef',
  ...alignLeftCellStyle,
  maxWidth: 140,
  minWidth: 125,
};

export const gaapNonMultiTotGaapGaapDefTaxColumnDefinition: ColumnDefinition = {
  headerName: 'GAAP Deferred Tax Expense',
  field: 'TotGaapGaapDefTax',
  ...alignLeftCellStyle,
  maxWidth: 140,
  minWidth: 125,
};

export const gaapNonMultiGaapCurTaxColumnDefinition: ColumnDefinition = {
  headerName: 'GAAP Cur Tax Exp Func Currency',
  field: 'gaapCurTax',
  ...alignLeftCellStyle,
  maxWidth: 140,
  minWidth: 125,
};

export const gaapNonMultiGaapTaxExpCyColumnDefinition: ColumnDefinition = {
  headerName: 'Tax Exp CY Activity Func Cur',
  field: 'gaapTaxExpCy',
  ...alignLeftCellStyle,
  maxWidth: 140,
  minWidth: 125,
};

export const gaapNonMultiGaapCyEffTaxRateColumnDefinition: ColumnDefinition = {
  headerName: 'CY Effective Tax Rate',
  field: 'gaapCyEffTaxRate',
  ...alignLeftCellStyle,
  maxWidth: 140,
  minWidth: 125,
};

export const gaapNonMultiTaxCurMorRateColumnDefinition: ColumnDefinition = {
  headerName: 'MOR',
  field: 'taxCurMor',
  ...alignLeftCellStyle,
  maxWidth: 140,
  minWidth: 125,
};

export const gaapNonMultiCurTaxExpTaxCurRateColumnDefinition: ColumnDefinition = {
  headerName: 'Cur Tax Exp Tax Currency',
  field: 'curTaxExpTaxCur',
  ...alignLeftCellStyle,
  maxWidth: 140,
  minWidth: 125,
};

export const gaapNonMultiGaapDefTaxExpTaxCurRateColumnDefinition: ColumnDefinition = {
  headerName: 'GAAP Deferred Tax Exp (TC)',
  field: 'gaapDefTaxExpTaxCur',
  ...alignLeftCellStyle,
  maxWidth: 140,
  minWidth: 125,
};

export const gaapNonMultiGaapTaxExpCyTaxCurRateColumnDefinition: ColumnDefinition = {
  headerName: 'Tax Exp CY Activity Tax Cur',
  field: 'gaapTaxExpCyTaxCur',
  ...alignLeftCellStyle,
  maxWidth: 140,
  minWidth: 125,
};

export const gaapNonMultiGaapRtaCurRateColumnDefinition: ColumnDefinition = {
  headerName: 'Return to Accrual (Current)',
  field: 'gaapRtaCur',
  ...alignLeftCellStyle,
  maxWidth: 140,
  minWidth: 125,
};

export const gaapNonMultiGapRtaDefColumnDefinition: ColumnDefinition = {
  headerName: 'Return to Accrual (Deferred)',
  field: 'gaapRtaDef',
  ...alignLeftCellStyle,
  maxWidth: 140,
  minWidth: 125,
};

export const gaapNonMultiGaapPraCurColumnDefinition: ColumnDefinition = {
  headerName: 'Post Return Adjust (Current)',
  field: 'gaapPraCur',
  ...alignLeftCellStyle,
  maxWidth: 140,
  minWidth: 125,
};

export const gaapNonMultiGaapPraDefColumnDefinition: ColumnDefinition = {
  headerName: 'Post Return Adjust (Deferred)',
  field: 'gaapPraDef',
  ...alignLeftCellStyle,
  maxWidth: 140,
  minWidth: 125,
};

export const gaapNonMultiGaapTrueUpPayColumnDefinition: ColumnDefinition = {
  headerName: 'True Up (Payable)',
  field: 'gaapTrueUpPay',
  ...alignLeftCellStyle,
  maxWidth: 140,
  minWidth: 125,
};

export const gaapNonMultiGaapTrueUpDefColumnDefinition: ColumnDefinition = {
  headerName: 'True Up (Deferred)',
  field: 'gaapTrueUpDef',
  ...alignLeftCellStyle,
  maxWidth: 140,
  minWidth: 125,
};

export const gaapNonMultiGaapRateChgDefColumnDefinition: ColumnDefinition = {
  headerName: 'Rate Changes (Deferred)',
  field: 'gaapRateChgDef',
  ...alignLeftCellStyle,
  maxWidth: 140,
  minWidth: 125,
};

export const gaapNonMultiGaapResCurColumnDefinition: ColumnDefinition = {
  headerName: 'Reserve (Current)',
  field: 'gaapResCur',
  ...alignLeftCellStyle,
  maxWidth: 140,
  minWidth: 125,
};

export const gaapNonMultiGaapResNonCurColumnDefinition: ColumnDefinition = {
  headerName: 'Reserve (Non Current)',
  field: 'gaapResNonCur',
  ...alignLeftCellStyle,
  maxWidth: 140,
  minWidth: 125,
};

export const gaapNonMultiGaapResPenColumnDefinition: ColumnDefinition = {
  headerName: 'Reserve (Penalty)',
  field: 'gaapResPen',
  ...alignLeftCellStyle,
  maxWidth: 140,
  minWidth: 125,
};

export const gaapNonMultiGaapOthProvDefColumnDefinition: ColumnDefinition = {
  headerName: 'Other Provision (Deferred)',
  field: 'gaapOthProvDef',
  ...alignLeftCellStyle,
  maxWidth: 140,
  minWidth: 125,
};

export const gaapNonMultiGaapTaxExpPyTaxCurColumnDefinition: ColumnDefinition = {
  headerName: 'GAAP Tax Expense on Prior Year Activity',
  field: 'gaapTaxExpPyTaxCur',
  ...alignLeftCellStyle,
  maxWidth: 140,
  minWidth: 125,
};

export const gaapNonMultiGaapCurTaxExpTotTaxCurColumnDefinition: ColumnDefinition = {
  headerName: 'GAAP Current Tax Expense - Total Activity',
  field: 'gaapCurTaxExpTotTaxCur',
  ...alignLeftCellStyle,
  maxWidth: 140,
  minWidth: 125,
};

export const gaapNonMultiGaapDefTaxExpTotTaxCurColumnDefinition: ColumnDefinition = {
  headerName: 'GAAP Deferred Tax Expense - Total Activity',
  field: 'gaapDefTaxExpTotTaxCur',
  ...alignLeftCellStyle,
  maxWidth: 140,
  minWidth: 125,
};

export const gaapNonMultiGaapTaxExpTaxCurColumnDefinition: ColumnDefinition = {
  headerName: 'Total Tax Expense Tax Currency',
  field: 'gaapTaxExpTaxCur',
  ...alignLeftCellStyle,
  maxWidth: 140,
  minWidth: 125,
};

export const gaapNonMultiGaapIbtTaxCurColumnDefinition: ColumnDefinition = {
  headerName: 'GAAP Inc Loss B4 Tax - Tax Cur',
  field: 'gaapIbtTaxCur',
  ...alignLeftCellStyle,
  maxWidth: 140,
  minWidth: 125,
};

export const gaapNonMultiGaapEtrColumnDefinition: ColumnDefinition = {
  headerName: 'GAAP Effective Tax Rate',
  field: 'gaapEtr',
  ...alignLeftCellStyle,
  maxWidth: 140,
  minWidth: 125,
};

export const adjAccountsEntAccountColumnDefinition: ColumnDefinition = {
  headerName: 'Ent Account',
  field: 'entAccount',
  ...alignLeftCellStyle,
  maxWidth: 140,
  minWidth: 125,
};

export const journalsAcctDescColumnDefinition: ColumnDefinition = {
  headerName: 'Account Description',
  field: 'acctDescriptiont',
  ...alignLeftCellStyle,
  maxWidth: 140,
  minWidth: 125,
};

export const newAccountColumnDefinition: ColumnDefinition = {
  headerName: 'New Account',
  field: 'newAccount',
  ...alignLeftCellStyle,
  maxWidth: 140,
  minWidth: 125,
};

export const ObligationsCttDescColumnDefinition: ColumnDefinition = {
  headerName: 'Country Tax Type Description',
  field: 'cttDescription',
  ...alignLeftCellStyle,
  maxWidth: 140,
  minWidth: 125,
};

export const ObligationsFedFtcLimitColumnDcefinition: ColumnDefinition = {
  headerName: 'Federal FTC Limit',
  field: 'fedFtcLimit',
  ...alignLeftCellStyle,
  maxWidth: 140,
  minWidth: 125,
};

export const ObligationsStateApptRateColumnDefinition: ColumnDefinition = {
  headerName: 'State Apportionment Rate',
  field: 'stateApptRate',
  ...alignLeftCellStyle,
  maxWidth: 140,
  minWidth: 125,
};

export const ObligationsCurTaxRateColumnDefinition: ColumnDefinition = {
  headerName: 'Current Tax Rate',
  field: 'curTaxRate',
  ...alignLeftCellStyle,
  maxWidth: 140,
  minWidth: 125,
};

export const statNonUsMultG2sAdjColumnDefinition: ColumnDefinition = {
  headerName: 'G2S Income/(Loss) Before Tax',
  field: 'g2sAdj',
  ...alignLeftCellStyle,
  maxWidth: 140,
  minWidth: 125,
};

export const statNonUsMultStatDefTaxExpColumnDefinition: ColumnDefinition = {
  headerName: 'STAT Deferred Tax Expense',
  field: 'statDefTaxExp',
  ...alignLeftCellStyle,
  maxWidth: 140,
  minWidth: 125,
};

export const statNonUsMultStatCurTaxColumnDefinition: ColumnDefinition = {
  headerName: 'STAT Cur Tax Expense Func Cur',
  field: 'statCurTax',
  ...alignLeftCellStyle,
  maxWidth: 140,
  minWidth: 125,
};

export const statNonUsMultTaxExpCyColumnDefinition: ColumnDefinition = {
  headerName: 'Tax Exp CY Act STAT Func Cur',
  field: 'taxExpCy',
  ...alignLeftCellStyle,
  maxWidth: 140,
  minWidth: 125,
};

export const gaapNonMultiStatCyEffTaxRateColumnDefinition: ColumnDefinition = {
  headerName: 'CY Effective Tax Rate',
  field: 'cyEffTaxRate',
  ...alignLeftCellStyle,
  maxWidth: 140,
  minWidth: 125,
};

export const statNonMultiDefTaxExpTaxCurRateColumnDefinition: ColumnDefinition = {
  headerName: 'Def Tax Expense Tax Cur',
  field: 'defTaxExpTaxCur',
  ...alignLeftCellStyle,
  maxWidth: 140,
  minWidth: 125,
};

export const statNonMultiTaxExpCyTaxCurRateColumnDefinition: ColumnDefinition = {
  headerName: 'Tax Exp CY Activity Tax Cur',
  field: 'taxExpCyTaxCur',
  ...alignLeftCellStyle,
  maxWidth: 140,
  minWidth: 125,
};

export const statNonMultiRtaCurRateColumnDefinition: ColumnDefinition = {
  headerName: 'Return to Accrual (Current)',
  field: 'rtaCur',
  ...alignLeftCellStyle,
  maxWidth: 140,
  minWidth: 125,
};

export const statNonMultiRtaDefColumnDefinition: ColumnDefinition = {
  headerName: 'Return to Accrual (Deferred)',
  field: 'rtaDef',
  ...alignLeftCellStyle,
  maxWidth: 140,
  minWidth: 125,
};

export const statNonMultiPraCurColumnDefinition: ColumnDefinition = {
  headerName: 'Post Return Adjust (Current)',
  field: 'praCur',
  ...alignLeftCellStyle,
  maxWidth: 140,
  minWidth: 125,
};

export const statNonMultiPraDefColumnDefinition: ColumnDefinition = {
  headerName: 'Post Return Adjust (Deferred)',
  field: 'praDef',
  ...alignLeftCellStyle,
  maxWidth: 140,
  minWidth: 125,
};

export const statNonMultiTrueUpPayColumnDefinition: ColumnDefinition = {
  headerName: 'True Up (Payable)',
  field: 'trueUpPay',
  ...alignLeftCellStyle,
  maxWidth: 140,
  minWidth: 125,
};

export const statNonMultiTrueUpDefColumnDefinition: ColumnDefinition = {
  headerName: 'True Up (Deferred)',
  field: 'trueUpDef',
  ...alignLeftCellStyle,
  maxWidth: 140,
  minWidth: 125,
};

export const statNonMultiRateChgDefColumnDefinition: ColumnDefinition = {
  headerName: 'Rate Changes (Deferred)',
  field: 'rateChgDef',
  ...alignLeftCellStyle,
  maxWidth: 140,
  minWidth: 125,
};

export const statNonMultiResCurColumnDefinition: ColumnDefinition = {
  headerName: 'Reserve (Current)',
  field: 'resCur',
  ...alignLeftCellStyle,
  maxWidth: 140,
  minWidth: 125,
};

export const statNonMultiResNonCurColumnDefinition: ColumnDefinition = {
  headerName: 'Reserve (Non Current)',
  field: 'resNonCur',
  ...alignLeftCellStyle,
  maxWidth: 140,
  minWidth: 125,
};

export const statNonMultiRespenColumnDefinition: ColumnDefinition = {
  headerName: 'Reserve (Penalty)',
  field: 'resPen',
  ...alignLeftCellStyle,
  maxWidth: 140,
  minWidth: 125,
};

export const statNonMultiGaapOthProvDefColumnDefinition: ColumnDefinition = {
  headerName: 'Other Provision (Deferred)',
  field: 'othProvDef',
  ...alignLeftCellStyle,
  maxWidth: 140,
  minWidth: 125,
};

export const statNonMultiTaxExpPyTaxCurColumnDefinition: ColumnDefinition = {
  headerName: 'Tax Exp on PY Activity Tax Cur',
  field: 'taxExpPyTaxCur',
  ...alignLeftCellStyle,
  maxWidth: 140,
  minWidth: 125,
};

export const statNonMultiStatCurTaxExpTotTaxCurColumnDefinition: ColumnDefinition = {
  headerName: 'STAT Cur Tax Exp Tot Activity',
  field: 'statCurTaxExpTotTaxCur',
  ...alignLeftCellStyle,
  maxWidth: 140,
  minWidth: 125,
};

export const statNonMultiStatDefTaxExpTotTaxCurColumnDefinition: ColumnDefinition = {
  headerName: 'STAT Def Tax Exp Tot Activity',
  field: 'statDefTaxExpTotTaxCur',
  ...alignLeftCellStyle,
  maxWidth: 140,
  minWidth: 125,
};

export const statNonMultiTaxExpTaxCurColumnDefinition: ColumnDefinition = {
  headerName: 'Total Tax Expense Tax Currency',
  field: 'taxExpTaxCur',
  ...alignLeftCellStyle,
  maxWidth: 140,
  minWidth: 125,
};

export const statNonMultiStatIbtTaxCurColumnDefinition: ColumnDefinition = {
  headerName: 'STAT Inc Loss B4 Tax - Tax Cur',
  field: 'statIbtTaxCur',
  ...alignLeftCellStyle,
  maxWidth: 140,
  minWidth: 125,
};

export const statNonMultiStatEtrColumnDefinition: ColumnDefinition = {
  headerName: 'STAT Effective Tax Rate',
  field: 'statEtr',
  ...alignLeftCellStyle,
  maxWidth: 140,
  minWidth: 125,
};

export const statNonMultiG2sDefTaxGColumnDefinition: ColumnDefinition = {
  headerName: 'G2S Deferred Tax Expense (G)',
  field: 'g2sDefTaxG',
  ...alignLeftCellStyle,
  maxWidth: 140,
  minWidth: 125,
};

export const taxRatePLAccountColumnDefinition: ColumnDefinition = {
  headerName: 'Current P&L',
  field: 'currentPL',
  ...alignLeftCellStyle,
  maxWidth: 140,
  minWidth: 125,
};

export const taxRateBsAccountColumnDefinition: ColumnDefinition = {
  headerName: 'Current BS',
  field: 'currentBS',
  ...alignLeftCellStyle,
  maxWidth: 140,
  minWidth: 125,
};

export const tlsAccountsentryDateColumnDefinition: ColumnDefinition = {
  headerName: 'Entry Date',
  field: 'entryDate',
  cellRenderer: DateCellRenderer,
  width: 140,
  minWidth: 125,
};

export const tlsUserListPrivilegeCodeColumnDefinition: ColumnDefinition = {
  headerName: 'Privilege',
  field: 'privilegeCode',
  ...alignLeftCellStyle,
  maxWidth: 140,
  minWidth: 125,
};

export const tlsUserListUserRoleColumnDefinition: ColumnDefinition = {
  headerName: 'User Role',
  field: 'userRole',
  ...alignCenterCellStyle,
  maxWidth: 140,
  minWidth: 125,
};

export const tlsUserListUserCreatedDateColumnDefinition: ColumnDefinition = {
  headerName: 'User Created Date',
  field: 'userCreatedDate',
  cellRenderer: DateCellRenderer,
  width: 140,
  minWidth: 125,
};

export const tlsUserListUserCreatedByColumnDefinition: ColumnDefinition = {
  headerName: 'User Created By',
  field: 'userCreatedBy',
  ...alignCenterCellStyle,
  width: 140,
  minWidth: 125,
};

export const tlsUserListUserLastLoginColumnDefinition: ColumnDefinition = {
  headerName: 'User Last Login',
  field: 'userLastLogin',
  cellRenderer: DateCellRenderer,
  width: 140,
  minWidth: 125,
};

export const trailBalFunctionalCurrencyColumnDefinition: ColumnDefinition = {
  headerName: 'Functional Currency Code',
  field: 'functionalCurrency',
  ...alignLeftCellStyle,
  maxWidth: 140,
  minWidth: 125,
};

export const trailBalDateTimeColumnDefinition: ColumnDefinition = {
  headerName: 'Date Time',
  field: 'dateTime',
  cellRenderer: DateCellRenderer,
  width: 140,
  minWidth: 125,
};

export const usFedMultiFuncCurrencyColumnDefinition: ColumnDefinition = {
  headerName: 'Functional Currency',
  field: 'funcCurrency',
  ...alignLeftCellStyle,
  maxWidth: 140,
  minWidth: 125,
};

export const usFedMultiIbtColumnDefinition: ColumnDefinition = {
  headerName: 'Income/(Loss) Before Tax',
  field: 'ibt',
  ...alignLeftCellStyle,
  maxWidth: 140,
  minWidth: 125,
};

export const usFedMultiStateCurTaxColumnDefinition: ColumnDefinition = {
  headerName: 'State Income Tax',
  field: 'stateCurTax',
  ...alignLeftCellStyle,
  maxWidth: 140,
  minWidth: 125,
};

export const usFedMultiStateCurTaxAdjColumnDefinition: ColumnDefinition = {
  headerName: 'State Adjustment',
  field: 'stateCurTaxAdj',
  ...alignLeftCellStyle,
  maxWidth: 140,
  minWidth: 125,
};

export const usFedMultiPermColumnDefinition: ColumnDefinition = {
  headerName: 'Total Permanent Diff',
  field: 'perm',
  ...alignLeftCellStyle,
  maxWidth: 140,
  minWidth: 125,
};

export const usFedMultiTempColumnDefinition: ColumnDefinition = {
  headerName: 'Total Temporary Diff',
  field: 'temp',
  ...alignLeftCellStyle,
  maxWidth: 140,
  minWidth: 125,
};

export const usFedMulticurTaxB4CrColumnDefinition: ColumnDefinition = {
  headerName: 'Current Exp Before Credits',
  field: 'curTaxB4Cr',
  ...alignLeftCellStyle,
  maxWidth: 140,
  minWidth: 125,
};

export const usFedMulticurFtcColumnDefinition: ColumnDefinition = {
  headerName: 'Foreign Current Tax Expense',
  field: 'curFtc',
  ...alignLeftCellStyle,
  maxWidth: 140,
  minWidth: 125,
};

export const usFedMultiDefFtcColumnDefinition: ColumnDefinition = {
  headerName: 'Foreign Deferred Tax Expense',
  field: 'defFtc',
  ...alignLeftCellStyle,
  maxWidth: 140,
  minWidth: 125,
};

export const usFedMultiTotFtcColumnDefinition: ColumnDefinition = {
  headerName: 'Total Foreign Taxes',
  field: 'totFtc',
  ...alignLeftCellStyle,
  maxWidth: 140,
  minWidth: 125,
};

export const usFedMultiFtcLimitColumnDefinition: ColumnDefinition = {
  headerName: 'Credit Limitation',
  field: 'ftcLimit',
  ...alignLeftCellStyle,
  maxWidth: 140,
  minWidth: 125,
};

export const usFedMultiBenFtcCrColumnDefinition: ColumnDefinition = {
  headerName: 'Total Benefitted Credits',
  field: 'benFtcCr',
  ...alignLeftCellStyle,
  maxWidth: 140,
  minWidth: 125,
};

export const usFedMultiCyTempColumnDefinition: ColumnDefinition = {
  headerName: 'Current Year Temporary Adjs',
  field: 'cyTemp',
  ...alignLeftCellStyle,
  maxWidth: 140,
  minWidth: 125,
};

export const usFedMultifbosColumnDefinition: ColumnDefinition = {
  headerName: 'Federal Benefit of State',
  field: 'fbos',
  ...alignLeftCellStyle,
  maxWidth: 140,
  minWidth: 125,
};

export const usFedMultitotDefColumnDefinition: ColumnDefinition = {
  headerName: 'Total Deferreds',
  field: 'totDef',
  ...alignLeftCellStyle,
  maxWidth: 140,
  minWidth: 125,
};

export const usFedMultiDefTaxB4FtcColumnDefinition: ColumnDefinition = {
  headerName: 'Deferred Tax Expense',
  field: 'defTaxB4Ftc',
  ...alignLeftCellStyle,
  maxWidth: 140,
  minWidth: 125,
};

export const usFedMultiCurTaxColumnDefinition: ColumnDefinition = {
  headerName: 'Current Exp - CY Activity',
  field: 'curTax',
  ...alignLeftCellStyle,
  maxWidth: 140,
  minWidth: 125,
};

export const usFedMultiDefTaxColumnDefinition: ColumnDefinition = {
  headerName: 'Deferred Exp - CY Activity',
  field: 'defTax',
  ...alignLeftCellStyle,
  maxWidth: 140,
  minWidth: 125,
};

export const usFedMultiTaxExpCyColumnDefinition: ColumnDefinition = {
  headerName: 'Tax Exp - CY Activity (FC)',
  field: 'taxExpCy',
  ...alignLeftCellStyle,
  maxWidth: 140,
  minWidth: 125,
};

export const usFedMultiCurTaxExpPyTaxCurColumnDefinition: ColumnDefinition = {
  headerName: 'Current Expense (TC)',
  field: 'curTaxExpPyTaxCur',
  ...alignLeftCellStyle,
  maxWidth: 140,
  minWidth: 125,
};

export const usFedMultiDefTaxExpPyTaxCurColumnDefinition: ColumnDefinition = {
  headerName: 'Deferred Tax Expense (TC)',
  field: 'defTaxExpPyTaxCur',
  ...alignLeftCellStyle,
  maxWidth: 140,
  minWidth: 125,
};

export const usFedMultiIbtTaxCurColumnDefinition: ColumnDefinition = {
  headerName: 'Income/(Loss) Before Tax (TC)',
  field: 'ibtTaxCur',
  ...alignLeftCellStyle,
  maxWidth: 140,
  minWidth: 125,
};

export const usFedMultiEtrColumnDefinition: ColumnDefinition = {
  headerName: 'Effective Tax Rate',
  field: 'etr',
  ...alignLeftCellStyle,
  maxWidth: 140,
  minWidth: 125,
};

export const usStateMultiUsTxblIncB4StateColumnDefinition: ColumnDefinition = {
  headerName: 'Fed Inc/(Loss) Before State',
  field: 'usTxblIncB4State',
  ...alignLeftCellStyle,
  maxWidth: 140,
  minWidth: 125,
};

export const usStateMultiStatePermModsColumnDefinition: ColumnDefinition = {
  headerName: 'Total State Tax Perm Mods',
  field: 'statePermMods',
  ...alignLeftCellStyle,
  maxWidth: 140,
  minWidth: 125,
};

export const usStateMultiStateTempModsColumnDefinition: ColumnDefinition = {
  headerName: 'Total State Tax Temp Mods',
  field: 'stateTempMods',
  ...alignLeftCellStyle,
  maxWidth: 140,
  minWidth: 125,
};

export const usStateMultiUtilizePyNolPreColumnDefinition: ColumnDefinition = {
  headerName: 'Utilize PY NOLS pre Appt',
  field: 'utilizePyNolPre',
  ...alignLeftCellStyle,
  maxWidth: 140,
  minWidth: 125,
};

export const usStateMultiStateTxblIncB4ApColumnDefinition: ColumnDefinition = {
  headerName: 'State Inc/(Loss) Before Appt',
  field: 'stateTxblIncB4Ap',
  ...alignLeftCellStyle,
  maxWidth: 140,
  minWidth: 125,
};

export const usStateMultiApRateColumnDefinition: ColumnDefinition = {
  headerName: 'Apportionment Rate',
  field: 'apRate',
  ...alignLeftCellStyle,
  maxWidth: 140,
  minWidth: 125,
};

export const usStateMultiApTxblIncColumnDefinition: ColumnDefinition = {
  headerName: 'Apportioned Taxable Inc/(Loss)',
  field: 'apTxblInc',
  ...alignLeftCellStyle,
  maxWidth: 140,
  minWidth: 125,
};

export const usStateMultiTxblIncAftNolColumnDefinition: ColumnDefinition = {
  headerName: 'Taxable Inc After NOL',
  field: 'txblIncAftNol',
  ...alignLeftCellStyle,
  maxWidth: 140,
  minWidth: 125,
};

export const usStateMultiStateModsColumnDefinition: ColumnDefinition = {
  headerName: 'State Modifications',
  field: 'stateMods',
  ...alignLeftCellStyle,
  maxWidth: 140,
  minWidth: 125,
};

export const usStateMultiDefTaxColumnDefinition: ColumnDefinition = {
  headerName: 'Deferred Tax Expense',
  field: 'defTax',
  ...alignLeftCellStyle,
  maxWidth: 140,
  minWidth: 125,
};

export const usStateMultiStateMinTaxColumnDefinition: ColumnDefinition = {
  headerName: 'Minimum Tax',
  field: 'stateMinTax',
  ...alignLeftCellStyle,
  maxWidth: 140,
  minWidth: 125,
};

export const usStateMultiMaxCurColumnDefinition: ColumnDefinition = {
  headerName: 'Total',
  field: 'maxCur',
  ...alignLeftCellStyle,
  maxWidth: 140,
  minWidth: 125,
};

export const currentPeriodCalcStatusColumnDefinition: ColumnDefinition = {
  headerName: 'Current Period Calc Status',
  field: 'currentPeriodCalcStatus',
  minWidth: 80,
  ...alignCenterCellStyle,
};

export const previousPeriodCalcStatusColumnDefinition: ColumnDefinition = {
  headerName: 'Previous Period Calc Status',
  field: 'previousPeriodCalcStatus',
  minWidth: 80,
  ...alignCenterCellStyle,
};

// Used on diagnostics page, not on admin pages
export const diagnosticsReportColumnDefinition: ColumnDefinition = {
  headerName: 'Diagnostic',
  field: 'report',
  minWidth: 300,
  customExport: {
    widthMultiplier: 0.15,
  },
  ...alignLeftCellStyle,
};

export const diagnosticsSeverityColumnDefinition: ColumnDefinition = {
  headerName: 'Severity',
  field: 'severity',
  minWidth: 100,
  customExport: {
    widthMultiplier: 0.25,
    mapValue: (value: unknown) => {
      if (value === '-1') {
        return 'Error';
      }
      if (value === '1') {
        return 'Warning';
      }
      if (value === '0') {
        return '';
      }
      return value;
    },
  },
  cellRenderer: DiagnosticsSeverityCellRenderer,
  ...alignCenterCellStyle,
};

export const diagnosticsSeverityWithTooltipColumnDefinition: ColumnDefinition = {
  ...diagnosticsSeverityColumnDefinition,
  headerName: 'Diagnostic',
  cellRendererParams: {
    tooltipContentField: 'diagnostic',
  },
};

export const fromCurrPrecColumnDefinition: ColumnDefinition = {
  headerName: 'From Curr Prec',
  field: 'fromCurrPrec',
  minWidth: 80,
  ...alignCenterCellStyle,
};

export const toCurrPrecColumnDefinition: ColumnDefinition = {
  headerName: 'To Curr Prec',
  field: 'toCurrPrec',
  minWidth: 80,
  ...alignCenterCellStyle,
};

export const leColumnDefinition: ColumnDefinition = {
  headerName: 'Le',
  field: 'le',
  minWidth: 80,
  ...alignCenterCellStyle,
};

export const tbeLeColumnDefinition: ColumnDefinition = {
  headerName: 'Tbe Le',
  field: 'tbeLe',
  minWidth: 80,
  ...alignCenterCellStyle,
};

// Used on admin pages
export const diagnosticColumnDefinition: ColumnDefinition = {
  headerName: 'Diagnostic',
  field: 'diagnostic',
  ...alignLeftCellStyle,
  minWidth: 200,
};

export const diagnosticWithTooltipColumnDefinition: ColumnDefinition = {
  ...diagnosticColumnDefinition,
  headerName: 'Diagnostic',
  cellRendererParams: {
    tooltipContentField: 'diagnostic',
  },
  cellRenderer: JSDiagnosticsReportCellRenderer as any,
};

export const adjustmentTypeDescription: ColumnDefinition = {
  headerName: 'Type Description',
  field: 'typeDescription',
  ...alignCenterCellStyle,
  minWidth: 140,
};

export const defPrCrUtlColumnDefinition: ColumnDefinition = {
  headerName: 'Total After Tax Deferred Tax Expense',
  field: 'defPrCrUtl',
  ...alignLeftCellStyle,
  maxWidth: 140,
  minWidth: 125,
};

export const gaapDefPrCrUtlColumnDefinition: ColumnDefinition = {
  headerName: 'Total After Tax Deferred Tax Expense',
  field: 'gaapDefPrCrUtl',
  ...alignLeftCellStyle,
  maxWidth: 140,
  minWidth: 125,
};

export const TotGaapDefTaxColumnDefinition: ColumnDefinition = {
  headerName: 'STAT Deferred Tax Expense',
  field: 'TotGaapDefTax',
  ...alignLeftCellStyle,
  maxWidth: 140,
  minWidth: 125,
};

export const defOthProvColumnDefinition: ColumnDefinition = {
  headerName: 'Other Provision (Def Only)',
  field: 'defOthProv',
  ...alignLeftCellStyle,
  maxWidth: 140,
  minWidth: 125,
};

export const netEndingColumnDefinition: ColumnDefinition = {
  headerName: 'Net Ending',
  field: 'endBalanceNet',
  minWidth: 100,
  cellRenderer: CustomRenderer,
  ...alignRightCellStyle,
};

export const grossEndingColumnDefinition: ColumnDefinition = {
  headerName: 'Gross Ending',
  field: 'endBalanceGross',
  minWidth: 100,
  cellRenderer: CustomRenderer,
  ...alignRightCellStyle,
};

export const netGrossRateColumnDefinition: ColumnDefinition = {
  headerName: 'Rate',
  field: 'netGrossRate',
  valueFormatter: PercentageValueFormatter,
  width: 105,
  minWidth: 105,
  ...alignRightCellStyle,
};

export const filingGroupColumnDefinition: ColumnDefinition = {
  headerName: 'State Filing Group',
  field: 'filingGroup',
  ...alignLeftCellStyle,
  maxWidth: 140,
  minWidth: 125,
};

export const resCurCyColumnDefinition: ColumnDefinition = {
  headerName: 'Reserve (Current)',
  field: 'resCurCy',
  ...alignLeftCellStyle,
  maxWidth: 140,
  minWidth: 125,
};

export const resNonCurCyColumnDefinition: ColumnDefinition = {
  headerName: 'Reserve (Non Current)',
  field: 'resNonCurCy',
  ...alignLeftCellStyle,
  maxWidth: 140,
  minWidth: 125,
};

export const resPenCyColumnDefinition: ColumnDefinition = {
  headerName: 'Reserve (Penalty)',
  field: 'resPenCy',
  ...alignLeftCellStyle,
  maxWidth: 140,
  minWidth: 125,
};

export const othProvCurColumnDefinition: ColumnDefinition = {
  headerName: 'Other Provision (Current)',
  field: 'othProvCur',
  ...alignLeftCellStyle,
  maxWidth: 140,
  minWidth: 125,
};

export const totCurAftTaxCyColumnDefinition: ColumnDefinition = {
  headerName: 'Tot Oth After Tax Adj - CY Activity (Cur) - Tax Curr',
  field: 'totCurAftTaxCy',
  ...alignLeftCellStyle,
  maxWidth: 140,
  minWidth: 125,
};

export const statTotCurAftTaxCyColumnDefinition: ColumnDefinition = {
  headerName: 'STAT Tot Oth After Tax Adj - CY Activity (Cur) - Tax Curr',
  field: 'statTotCurAftTaxCy',
  ...alignLeftCellStyle,
  maxWidth: 140,
  minWidth: 125,
};

export const totDefAftTaxCyColumnDefinition: ColumnDefinition = {
  headerName: 'Tot Oth After Tax Adj - CY Activity (Def) Tax Curr',
  field: 'totDefAftTaxCy',
  ...alignLeftCellStyle,
  maxWidth: 140,
  minWidth: 125,
};

export const statTotDefAftTaxCyColumnDefinition: ColumnDefinition = {
  headerName: 'STAT Tot Oth After Tax Adj - CY Activity (Def) Tax Curr',
  field: 'statTotDefAftTaxCy',
  ...alignLeftCellStyle,
  maxWidth: 140,
  minWidth: 125,
};

export const totCurTaxExpCyColumnDefinition: ColumnDefinition = {
  headerName: 'Tot Cur Tax Exp on CY Activity - Tax Curr',
  field: 'totCurTaxExpCy',
  ...alignLeftCellStyle,
  maxWidth: 140,
  minWidth: 125,
};

export const statTotCurTaxExpCyColumnDefinition: ColumnDefinition = {
  headerName: 'STAT Tot Cur Tax Exp on CY Activity - Tax Curr',
  field: 'statTotCurTaxExpCy',
  ...alignLeftCellStyle,
  maxWidth: 140,
  minWidth: 125,
};

export const totDefTaxExpCyColumnDefinition: ColumnDefinition = {
  headerName: 'Tot Def Tax Exp on CY Activity - Tax Curr',
  field: 'totDefTaxExpCy',
  ...alignLeftCellStyle,
  maxWidth: 140,
  minWidth: 125,
};

export const statTotDefTaxExpCyColumnDefinition: ColumnDefinition = {
  headerName: 'STAT Tot Def Tax Exp on CY Activity - Tax Curr',
  field: 'statTotDefTaxExpCy',
  ...alignLeftCellStyle,
  maxWidth: 140,
  minWidth: 125,
};

export const totTaxExpCyColumnDefinition: ColumnDefinition = {
  headerName: 'Tot Tax Exp on CY Activity - Tax Curr',
  field: 'totTaxExpCy',
  ...alignLeftCellStyle,
  maxWidth: 140,
  minWidth: 125,
};

export const statTotTaxExpCyColumnDefinition: ColumnDefinition = {
  headerName: 'STAT Tot Tax Exp on CY Activity - Tax Curr',
  field: 'statTotTaxExpCy',
  ...alignLeftCellStyle,
  maxWidth: 140,
  minWidth: 125,
};

export const totCurAftTaxPyColumnDefinition: ColumnDefinition = {
  headerName: 'Tot Oth After Tax Adj - PY Activity (Cur) - Tax Curr',
  field: 'totCurAftTaxPy',
  ...alignLeftCellStyle,
  maxWidth: 140,
  minWidth: 125,
};

export const statTotCurAftTaxPyColumnDefinition: ColumnDefinition = {
  headerName: 'STAT Tot Oth After Tax Adj - PY Activity (Cur) - Tax Curr',
  field: 'statTotCurAftTaxPy',
  ...alignLeftCellStyle,
  maxWidth: 140,
  minWidth: 125,
};

export const totDefAftTaxPyColumnDefinition: ColumnDefinition = {
  headerName: 'Tot Oth After Tax Adj - PY Activity (Def) - Tax Curr',
  field: 'totDefAftTaxPy',
  ...alignLeftCellStyle,
  maxWidth: 140,
  minWidth: 125,
};

export const statTotDefAftTaxPyColumnDefinition: ColumnDefinition = {
  headerName: 'STAT Tot Oth After Tax Adj - PY Activity (Def) - Tax Curr',
  field: 'statTotDefAftTaxPy',
  ...alignLeftCellStyle,
  maxWidth: 140,
  minWidth: 125,
};

export const taxExpPyTaxCurColumnDefinition: ColumnDefinition = {
  headerName: 'Total Tax Expense on PY Activity - Tax Currency',
  field: 'taxExpPyTaxCur',
  ...alignLeftCellStyle,
  maxWidth: 140,
  minWidth: 125,
};

export const statTaxExpPyTaxCurColumnDefinition: ColumnDefinition = {
  headerName: 'STAT Tot Tax Exp on PY Activity - Tax Curr',
  field: 'statTaxExpPyTaxCur',
  ...alignLeftCellStyle,
  maxWidth: 140,
  minWidth: 125,
};

export const gaapResCurCyColumnDefinition: ColumnDefinition = {
  headerName: 'Reserve (Current)',
  field: 'gaapResCurCy',
  ...alignLeftCellStyle,
  maxWidth: 140,
  minWidth: 125,
};

export const gaapResNonCurCyColumnDefinition: ColumnDefinition = {
  headerName: 'Reserve (Non Current)',
  field: 'gaapResNonCurCy',
  ...alignLeftCellStyle,
  maxWidth: 140,
  minWidth: 125,
};

export const gaapResPenCyColumnDefinition: ColumnDefinition = {
  headerName: 'Reserve (Penalty)',
  field: 'gaapResPenCy',
  ...alignLeftCellStyle,
  maxWidth: 140,
  minWidth: 125,
};

export const gaapOthProvCurColumnDefinition: ColumnDefinition = {
  headerName: 'Other Provision (Current)',
  field: 'gaapOthProvCur',
  ...alignLeftCellStyle,
  maxWidth: 140,
  minWidth: 125,
};

export const gaapTotCurAftTaxCyColumnDefinition: ColumnDefinition = {
  headerName: 'GAAP Tot Oth After Tax Adj - CY Activity (Cur) - Tax Curr',
  field: 'gaapTotCurAftTaxCy',
  ...alignLeftCellStyle,
  maxWidth: 140,
  minWidth: 125,
};

export const gaapTotDefAftTaxCyColumnDefinition: ColumnDefinition = {
  headerName: 'GAAP Tot Oth After Tax Adj - CY Activity (Def) Tax Curr',
  field: 'gaapTotDefAftTaxCy',
  ...alignLeftCellStyle,
  maxWidth: 140,
  minWidth: 125,
};

export const gaapTotCurTaxExpCyColumnDefinition: ColumnDefinition = {
  headerName: 'GAAP Tot Cur Tax Exp on CY Activity - Tax Curr',
  field: 'gaapTotCurTaxExpCy',
  ...alignLeftCellStyle,
  maxWidth: 140,
  minWidth: 125,
};

export const gaapTotDefTaxExpCyColumnDefinition: ColumnDefinition = {
  headerName: 'GAAP Tot Def Tax Exp on CY Activity - Tax Curr',
  field: 'gaapTotDefTaxExpCy',
  ...alignLeftCellStyle,
  maxWidth: 140,
  minWidth: 125,
};

export const gaapTotTaxExpCyColumnDefinition: ColumnDefinition = {
  headerName: 'GAAP Tot Tax Exp on CY Activity - Tax Curr',
  field: 'gaapTotTaxExpCy',
  ...alignLeftCellStyle,
  maxWidth: 140,
  minWidth: 125,
};

export const gaapTotCurAftTaxPyColumnDefinition: ColumnDefinition = {
  headerName: 'GAAP Tot Oth After Tax Adj - PY Activity (Cur) - Tax Curr',
  field: 'gaapTotCurAftTaxPy',
  ...alignLeftCellStyle,
  maxWidth: 140,
  minWidth: 125,
};

export const gaapTotDefAftTaxPyColumnDefinition: ColumnDefinition = {
  headerName: 'GAAP Tot Oth After Tax Adj - PY Activity (Def) - Tax Curr',
  field: 'gaapTotDefAftTaxPy',
  ...alignLeftCellStyle,
  maxWidth: 140,
  minWidth: 125,
};


export const resIntColumnDefinition: ColumnDefinition = {
  headerName: 'Reserve (Interest)',
  field: 'resInt',
  ...alignLeftCellStyle,
  maxWidth: 140,
  minWidth: 125,
};

export const resIntCyColumnDefinition: ColumnDefinition = {
  headerName: 'Reserve (Interest)',
  field: 'resIntCy',
  ...alignLeftCellStyle,
  maxWidth: 140,
  minWidth: 125,
};

export const gaapResIntColumnDefinition: ColumnDefinition = {
  headerName: 'Reserve (Interest)',
  field: 'gaapResInt',
  ...alignLeftCellStyle,
  maxWidth: 140,
  minWidth: 125,
};

export const gaapResIntCyColumnDefinition: ColumnDefinition = {
  headerName: 'Reserve (Interest)',
  field: 'gaapResIntCy',
  ...alignLeftCellStyle,
  maxWidth: 140,
  minWidth: 125,
};

export const stateTxblIncColumnDefinition: ColumnDefinition = {
  headerName: 'State Inc/(Loss) Before Pre-Appt NOL',
  field: 'stateTxblInc',
  ...alignLeftCellStyle,
  maxWidth: 140,
  minWidth: 125,
};

export const payableTagColumnDefinition: ColumnDefinition = {
  headerName: 'Payable Group',
  field: 'payableTag',
  ...alignLeftCellStyle,
  maxWidth: 140,
  minWidth: 125,
};

export const expirationDateColumnDefinition: ColumnDefinition = {
  headerName: 'Expiration Date',
  field: 'expirationDate',
  cellRenderer: DateCellRenderer,
  width: 140,
  minWidth: 125,
};

export const expirationUpdatedOnColumnDefinition: ColumnDefinition = {
  headerName: 'Expiration Updated On',
  field: 'expirationUpdatedOn',
  cellRenderer: DateCellRenderer,
  width: 140,
  minWidth: 125,
};

export const expirationUpdatedBy: ColumnDefinition = {
  headerName: 'Expiration Updated By',
  field: 'expirationUpdatedBy',
  ...alignLeftCellStyle,
  minWidth: 120,
};

export const expirationSsoName: ColumnDefinition = {
  ...expirationUpdatedBy,
  field: 'expirationSsoName',
};

export const refreshedOn: ColumnDefinition = {
  headerName: 'Refreshed On',
  field: 'refreshedOn',
  cellRenderer: DateCellRenderer,
  width: 140,
  minWidth: 125,
};

export const roleUpdatedOn: ColumnDefinition = {
  headerName: 'Role Updated On',
  field: 'roleUpdatedOn',
  cellRenderer: DateCellRenderer,
  minWidth: 100,
};
